/**
 * ResolveIssuesView — Fix & Continue workspace.
 *
 * Layout: slim header → outer Allotment (sidebar | editor).
 * AI panel is an overlay drawer (does NOT shrink editors).
 * The outer Allotment gives CodeEditorView a concrete height so its
 * inner Allotment (Sections | COBOL | Java) renders correctly.
 *
 * REUSE CONTRACT:
 *   - Editor surface: <CodeEditorView hideHeader={true} externalNavTarget={...} />
 *     Same Monaco instance, same Command Palette (Cmd+Shift+P), same AI actions.
 *   - Cmd+Shift+R in the editor still opens AIResponseModal (unchanged).
 *   - "Ask AI for Suggestion" button opens the inline AI panel (right drawer)
 *     which calls aiService.customPrompt() directly — no modal overlay.
 *   - externalNavTarget navigates by filename + line using revealLineInCenter().
 *
 * DATA:
 *   Issues loaded from real gate artifacts via gateReportService.js → gateIssueNormalizer.js.
 *   Structural: GET /java-validation/results/json/{jobId}/java_validation_report.json
 *   Compile:    GET /pre-flight/results/json/{jobId}/compile_result.json (+ snippets)
 *   Editor: CodeEditorView loads COBOL/Java files from the real editor API.
 *
 * AI PANEL:
 *   Reuses existing aiService.customPrompt() → POST /ai/chat (purpose='custom')
 *   → Bedrock Claude 3.5 Sonnet.  Apply persists via applyFix() (CUSTOM_EDIT).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Allotment } from 'allotment'
import {
  ArrowLeft, ShieldCheck, Zap, Wrench, Clock, MessageSquare,
  CheckCircle, Loader2, XCircle, Check, FileCode, AlertTriangle,
  PanelLeft, PanelLeftClose, Sparkles, Lightbulb, ChevronDown, ChevronsDown,
  Activity, Wand2, Puzzle, Package, ChevronRight, ExternalLink,
  Save, Play, Info, RefreshCw
} from 'lucide-react'
import CodeEditorView from './CodeEditorView'
import ProjectExplorer from '../editor/ProjectExplorer'
import EditorTabBar from '../editor/EditorTabBar'
import AISuggestionModal from '../editor/AISuggestionModal'
import ExplainIssueModal from '../editor/ExplainIssueModal'
import { useAIResolve } from '../../hooks/useAIResolve'
import AIResolveModal from '../editor/AIResolveModal'
import { fetchIssues } from '../../services/gates/IssueService'
import { useIssueResolution } from '../../hooks/useIssueResolution'
import { fetchDecisions, upsertDecision } from '../../services/gates/gateDecisionService'
import { editorCreateFile } from '../../services/workspaceService'
// AI panel reuses the same backend as Command Palette AI actions (POST /ai/chat)
import { customPrompt as customPromptAI } from '../../services/aiService'
import { getAPIBaseUrl, isWebPortal } from '../../environment'
import { getAuthHeaders } from '../../services/nodeExecutors/authHelper'
import { findAnchorLine } from '../../utils/anchorMatch'
import { findEnclosingMethod, buildMethodStub } from '../../utils/javaStructureParser'
import { inferMissingSymbol } from '../../utils/inferMissingSymbol'
import { fetchSymbolIndex, rebuildSymbolIndex, groupIssuesBySymbol, getSymbolHint } from '../../services/javaSymbolIndexService'
import 'allotment/dist/style.css'
import WorkbenchShell from '../workbench/WorkbenchShell'
import './ResolveIssuesView.css'

const GATE_LABELS = {
  structural: 'Structural Check',
  compile:    'Compile Check',
  package:    'Package Compile Check',
}

// CodeEditorView source prop per gate type.
// Structural loads service-generated Java; compile loads from compile_failure_sources/.
// Package loads from java_packaging compile_failure_sources/.
const EDITOR_SOURCE = {
  structural: 'service-generation',
  compile:    'compile',
  package:    'package',
}

// UTF-8-safe base64 encode.  Raw btoa() throws InvalidCharacterError on any
// non-Latin1 character (e.g. Unicode in Java comments or Maven original_line output).
// encodeURIComponent → percent-encoded UTF-8 bytes → unescape back to Latin1 → btoa.
function safeBase64(str) {
  return btoa(unescape(encodeURIComponent(str)))
}

// ─────────────────────────────────────────────────────────────────────────────
// SaveAndRerunModal — shown when Re-run is clicked with unsaved editor changes
// ─────────────────────────────────────────────────────────────────────────────

function SaveAndRerunModal({ onSaveAndRerun, onRerunWithout, onCancel }) {
  return (
    <div className="rv-modal-overlay" onClick={onCancel}>
      <div className="rv-modal-box" onClick={e => e.stopPropagation()}>
        <h3 className="rv-modal-title">Unsaved Changes</h3>
        <p className="rv-modal-body">
          The editor has unsaved changes. Save them before re-running?
        </p>
        <div className="rv-modal-actions">
          <button className="rv-fix-btn primary" onClick={onSaveAndRerun}>Save &amp; Re-run</button>
          <button className="rv-fix-btn"         onClick={onRerunWithout}>Re-run without saving</button>
          <button className="rv-fix-btn"         onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// IssueRow
// ─────────────────────────────────────────────────────────────────────────────

// Detect "cannot find symbol" or "package does not exist" errors that can have stubs generated
const STUB_ERROR_RE = /cannot find symbol|package does not exist/i

// Extract a class name hint from a "cannot find symbol" message.
// The summary may contain the merged Maven continuation lines, e.g.:
//   "cannot find symbol\n  symbol: class BigDecimal\n  location: ..."
// Try the merged form first, then the inline form.
function extractStubFQNHint(summary) {
  if (!summary) return ''
  // Merged Maven form: "symbol: class Foo" on its own segment
  const merged = /symbol\s*:\s*(?:class|interface|enum)\s+([\w.]+)/i.exec(summary)
  if (merged) return merged[1]
  // Inline form: "cannot find symbol ... class Foo"
  const inline = /cannot find symbol.*?(?:class|interface|type)\s+([\w.]+)/i.exec(summary)
  if (inline) return inline[1]
  return ''
}

/**
 * Build stub code for a field/array/method kind and determine the insertion line.
 * Returns { stubCode, insertLine } (insertLine is 1-indexed).
 *
 * Insertion strategy (conservative, avoids guessing method bodies):
 *  1. Find the last field declaration line (private/public/protected ... ;).
 *  2. If none found, insert after the class opening brace.
 *  3. For method stubs, insert before the class closing brace.
 */
function buildFieldStub(symbolName, kind, fieldType, fileContent) {
  const lines = fileContent.split('\n')
  const tok = symbolName || 'stub'
  const type = fieldType || 'Object'

  let stubCode = ''
  if (kind === 'field') {
    stubCode = `    private ${type} ${tok}; // COMPILE-ONLY STUB — set correct type`
  } else if (kind === 'array') {
    stubCode = `    private ${type}[] ${tok} = new ${type}[0]; // COMPILE-ONLY STUB — set correct type`
  } else if (kind === 'method') {
    stubCode = `    // COMPILE-ONLY STUB — replace with real implementation\n    private Object ${tok}() {\n        throw new UnsupportedOperationException("Stub: ${tok} not implemented");\n    }`
  } else {
    stubCode = `    private Object ${tok}; // COMPILE-ONLY STUB — set correct type`
  }

  let lastFieldLine = -1
  let classOpenLine = -1
  let classCloseLine = lines.length - 1

  for (let i = 0; i < lines.length; i++) {
    const trim = lines[i].trim()
    if (classOpenLine === -1 && /^(public|private|protected|abstract|final|\s)*(class|enum|interface)\s+\w/.test(trim)) {
      for (let j = i; j < Math.min(i + 5, lines.length); j++) {
        if (lines[j].includes('{')) { classOpenLine = j + 1; break }
      }
    }
    if (/^\s*(private|protected|public)\s+[\w<>\[\].,\s]+\s+\w[\w,\s]*;/.test(lines[i])) {
      lastFieldLine = i + 1
    }
    if (trim === '}' && classOpenLine > 0) {
      classCloseLine = i + 1
    }
  }

  let insertLine
  if (kind === 'method') {
    insertLine = Math.max(classCloseLine - 1, classOpenLine > 0 ? classOpenLine + 1 : 2)
  } else {
    insertLine = lastFieldLine > 0
      ? lastFieldLine + 1
      : classOpenLine > 0
        ? classOpenLine + 1
        : 3
  }

  return { stubCode, insertLine }
}

function IssueRow({ issue, selected, resolution, onClick, onGenerateStub }) {
  const isResolved   = resolution === 'pending_patch'
  const isDeferred   = resolution === 'deferred'
  const isSuppressed = resolution === 'suppressed'
  const isStubbed    = resolution === 'stubbed'
  const isExternal   = resolution === 'external'
  const showStubBtn  = onGenerateStub && STUB_ERROR_RE.test(issue?.summary || '')

  return (
    <div className={[
      'rv-issue-row-wrap',
      selected ? 'selected' : '',
    ].filter(Boolean).join(' ')}>
      <button
        className={[
          'rv-issue-row',
          selected     ? 'selected'    : '',
          isResolved   ? 'resolved'    : '',
          isDeferred   ? 'deferred'    : '',
          isSuppressed ? 'suppressed'  : '',
          isStubbed    ? 'stubbed'     : '',
          isExternal   ? 'external'    : '',
        ].filter(Boolean).join(' ')}
        onClick={onClick}
        title={issue.summary}
      >
        <span className={`rv-issue-sev ${issue.severity === 'BLOCK' ? 'block' : 'warn'}`}>
          {issue.severity === 'BLOCK'
            ? <XCircle size={11} />
            : <AlertTriangle size={11} />
          }
        </span>
        <span className="rv-issue-text">{issue.summary}</span>
        {isResolved   && <Check   size={11} className="rv-issue-state resolved"   title="Fix recorded" />}
        {isDeferred   && <Clock   size={11} className="rv-issue-state deferred"   title="Marked TODO" />}
        {isSuppressed && <Check   size={11} className="rv-issue-state suppressed" title="Suppressed" />}
        {isStubbed    && <Puzzle  size={11} className="rv-issue-state stubbed"    title="Stub scheduled (compile-only)" />}
        {isExternal   && <Package size={11} className="rv-issue-state external"   title="External dependency recorded" />}
      </button>
      {showStubBtn && (
        <button
          className="rv-stub-inline-btn"
          onClick={e => { e.stopPropagation(); onGenerateStub(issue) }}
          title="Generate a compile-only stub class for this missing symbol"
        >
          <Puzzle size={10} /> Stub
        </button>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// EditorPlaceholder — shown when no job context is available
// ─────────────────────────────────────────────────────────────────────────────

function EditorPlaceholder({ gateType }) {
  return (
    <div className="rv-editor-placeholder">
      <FileCode size={40} className="rv-placeholder-icon" />
      <p className="rv-placeholder-title">No code context loaded</p>
      <p className="rv-placeholder-body">
        Run the {GATE_LABELS[gateType] || 'gate check'} pipeline to load COBOL
        and Java files here. Once the job completes, selecting an issue will
        open the relevant files in the editor.
      </p>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// isStructuralError
//
// Returns true when the issue summary matches patterns that indicate a broken
// Java file structure (package/import inside a method, missing class declaration,
// brace drift, dangling else/catch, etc.).
//
// Single-line stubs worsen these errors by shifting lines in already-broken
// contexts.  Structural errors should use AI Repair Method / Region / File.
// ─────────────────────────────────────────────────────────────────────────────
const STRUCTURAL_ERROR_RE =
  /class[\s,]*interface[\s,]*enum|not a statement|illegal start of (expression|type)|reached end of file|'class' expected|class.*expected|record.*expected|orphan.*catch|else without if/i

function isStructuralError(issue) {
  return STRUCTURAL_ERROR_RE.test(issue?.summary || '')
}

// ─────────────────────────────────────────────────────────────────────────────
// runQuickCheck — client-side structural sanity check on Java file content.
//
// Catches the three most common issues that make Maven compile useless:
//   1. Brace imbalance  (every { needs a matching })
//   2. package/import appearing AFTER the class declaration
//   3. File not ending with } (orphaned code / missing class close)
//
// Returns { pass: boolean, errors: [{line: number|null, message: string}] }
// Pure function — no side effects, runs in <1 ms.
// ─────────────────────────────────────────────────────────────────────────────

const CLASS_DECL_RE  = /^\s*(?:(?:public|protected|private|abstract|final|static|strictfp)\s+)*class\s+\w/
const PACKAGE_IMPORT_RE = /^\s*(package|import)\s+\w/

function runQuickCheck(fileContent) {
  if (!fileContent) {
    return { pass: false, errors: [{ line: null, message: 'No file content loaded in editor.' }] }
  }

  const errors = []
  const lines  = fileContent.split('\n')

  // ── 1. Brace balance ──────────────────────────────────────────────────────
  const openCount  = (fileContent.match(/\{/g) || []).length
  const closeCount = (fileContent.match(/\}/g) || []).length
  const balance    = openCount - closeCount
  if (balance !== 0) {
    errors.push({
      line: null,
      message: `Brace imbalance: ${balance > 0
        ? `+${balance} extra { (need ${balance} more })`
        : `${Math.abs(balance)} missing { (${Math.abs(balance)} too many })`
      }. Must be 0 to compile.`,
    })
  }

  // ── 2. File must end with } ───────────────────────────────────────────────
  if (!fileContent.trimEnd().endsWith('}')) {
    errors.push({
      line: lines.length,
      message: 'File does not end with "}". Class closing brace may be missing or orphaned code follows it.',
    })
  }

  // ── 3. package/import must not appear after class declaration ────────────
  let classLineIdx = -1
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]
    if (/^\s*(\/\/|\*)/.test(raw)) continue   // skip comment lines
    if (CLASS_DECL_RE.test(raw)) { classLineIdx = i; break }
  }
  if (classLineIdx !== -1) {
    for (let i = classLineIdx + 1; i < lines.length; i++) {
      if (PACKAGE_IMPORT_RE.test(lines[i])) {
        errors.push({
          line: i + 1,
          message: `package/import at line ${i + 1} appears after the class declaration. Move all imports to the top of the file.`,
        })
        break   // first occurrence is enough
      }
    }
  }

  // ── 4. package statement should be near the top ───────────────────────────
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim()
    if (!t || /^(\/\/|\*)/.test(t)) continue  // skip blank / comment
    if (/^package\s+/.test(t)) {
      if (i > 5) {
        errors.push({
          line: i + 1,
          message: `package declaration at line ${i + 1} (expected near line 1). File structure is likely broken.`,
        })
      }
      break
    }
    break  // first non-blank non-comment line is not package — that's fine
  }

  return { pass: errors.length === 0, errors }
}

// ─────────────────────────────────────────────────────────────────────────────
// QuickCheckRerunModal — shown when Re-run is triggered after a failed Quick Check
// ─────────────────────────────────────────────────────────────────────────────

function QuickCheckRerunModal({ errors, onRerunAnyway, onCancel }) {
  return (
    <div className="rv-modal-overlay" onClick={onCancel}>
      <div className="rv-modal-box" onClick={e => e.stopPropagation()}>
        <h3 className="rv-modal-title">Quick Check Found Issues</h3>
        <p className="rv-modal-body">
          These structural problems may cause the compile to fail or produce misleading errors:
        </p>
        <ul className="rv-qc-error-list">
          {errors.map((e, i) => (
            <li key={i}>{e.line ? `Line ${e.line}: ` : ''}{e.message}</li>
          ))}
        </ul>
        <p className="rv-modal-body" style={{ marginTop: 10 }}>
          Fix these first for faster results, or re-run anyway (takes 1–3 min).
        </p>
        <div className="rv-modal-actions">
          <button className="rv-fix-btn primary" onClick={onRerunAnyway}>Re-run Anyway</button>
          <button className="rv-fix-btn"         onClick={onCancel}>Cancel — Fix Issues First</button>
        </div>
      </div>
    </div>
  )
}

// FixPanel
// ─────────────────────────────────────────────────────────────────────────────

function FixPanel({
  issue, resolution,
  onApplyFix, onMarkDeferred, onExplainIssue, onCommentOutLine, onBlockCommentSelection,
  onAskAI, onAIRepairMethod, onAIRepairRegion, onAIRepairFileStructure,
  rerunning, onRerun, actionMsg,
  onQuickCheck, quickChecking, quickCheckResult,
  onAIResolveAuto, aiResolving,
  onFixNow, onGenerateStub, onMarkExternal,
  onStrictCheck,
  symbolHint,
}) {
  const [showOtherOptions, setShowOtherOptions] = useState(true)

  if (!issue) {
    return (
      <div className="rv-fix-panel empty">
        <div className="rv-fix-empty">Select an issue to see actions</div>
      </div>
    )
  }

  const isStructural    = isStructuralError(issue)
  const isMissingSymbol = !!issue.symbolCategory  // MISSING_TYPE | MISSING_METHOD | MISSING_FIELD | MISSING_IMPORT

  // For "cannot find symbol" errors: if the normalizer already resolved symbolName via
  // inferMissingSymbol, use it directly. Otherwise attempt a fresh inference here so
  // the Fix Panel always shows the best available token even on older cached issues.
  const inferred = (!issue.symbolName && isMissingSymbol)
    ? inferMissingSymbol(issue)
    : null
  const displaySymbolName = issue.symbolName || inferred?.token || null
  const displayKind       = issue.inferredKind || inferred?.kind || null

  // Map inferredKind → symbolCategory override for sub-note text
  const effectiveKindLabel = (() => {
    if (displayKind === 'field')  return 'MISSING_FIELD'
    if (displayKind === 'array')  return 'MISSING_FIELD_ARRAY'
    if (displayKind === 'method') return 'MISSING_METHOD'
    if (displayKind === 'class')  return 'MISSING_TYPE'
    return issue.symbolCategory
  })()

  // Which primary action to promote — drives the RECOMMENDED section
  const primaryAction = (() => {
    if (issue.fixMode === 'allowlisted_auto_fix') return 'auto_fix'
    if (isMissingSymbol) return 'stub_and_external'
    if (isStructural)    return 'ai_repair_method'
    return 'ai_resolve'
  })()

  // Plain-English "what is this?" per issue type
  const whatIsThisText = (() => {
    if (isMissingSymbol) {
      const sym = displaySymbolName ? `"${displaySymbolName}"` : 'a symbol'
      const kindMap = { class: `class ${sym}`, field: `field ${sym}`, array: `array field ${sym}`, method: `method ${sym}` }
      return `The compiler cannot find ${kindMap[displayKind] || sym}. It may be missing from the project, or it may come from an external library.`
    }
    if (isStructural) {
      return 'A structural Java problem the compiler cannot parse — brace imbalance, misplaced package or import, or a COBOL pattern that did not translate cleanly. Fixing this may reveal more errors below it; that is normal.'
    }
    const s = (issue.summary || '').toLowerCase()
    if (s.includes('incompatible types') || s.includes('no suitable method') || s.includes('bad return type') || s.includes('cannot be converted')) {
      return "A type mismatch: the code uses a value where the compiler expects a different type. COBOL's loose typing often causes this during translation."
    }
    return 'A Java compile error preventing the build from completing.'
  })()

  // Sub-note below the Generate Stub CTA button
  const stubSubnote = {
    MISSING_TYPE:        'Creates a compile-only placeholder class → written to workspace',
    MISSING_METHOD:      'Inserts a compile-only stub method into the open file → written to workspace',
    MISSING_FIELD:       'Inserts a compile-only stub field into the open file → written to workspace',
    MISSING_FIELD_ARRAY: 'Inserts a compile-only stub array field into the open file → written to workspace',
    MISSING_IMPORT:      'Creates a compile-only placeholder class for the import → written to workspace',
  }[effectiveKindLabel] || 'Creates a compile-only stub so the build can proceed → written to workspace'

  return (
    <div className="rv-fix-panel">

      {/* ── HEADING ── */}
      <div className="rv-fix-heading">
        <Wrench size={12} />
        Fix Panel
      </div>

      {/* ── ISSUE ── */}
      <div className="rv-fix-issue-block">
        <div className="rv-fix-summary">{issue.summary}</div>
        {issue.javaLocation?.file && (
          <div className="rv-fix-issue-loc">
            {issue.javaLocation.file}
            {issue.javaLocation.line != null && (
              <span className="rv-fix-issue-line"> · line {issue.javaLocation.line}</span>
            )}
          </div>
        )}
        {isStructural && (
          <div className="rv-structural-warning">
            <AlertTriangle size={10} />
            Structural error — fixing this may reveal more errors below. That is normal.
          </div>
        )}
      </div>

      {/* ── WHAT IS THIS? ── */}
      <div className="rv-fix-section rv-fix-section--what">
        <div className="rv-fix-section-hd">
          <span className="rv-fix-section-hd-label">What is this?</span>
          <button
            className="rv-fix-btn--explain-inline"
            onClick={onExplainIssue}
            title="AI explanation of what this error means and how to fix it"
          >
            <Lightbulb size={11} />
            Explain with AI
          </button>
        </div>
        <p className="rv-fix-what-is-this">{whatIsThisText}</p>

        {/* Symbol details */}
        {isMissingSymbol && displaySymbolName && (
          <div className="rv-fix-symbol-row">
            <Puzzle size={10} className="rv-fix-symbol-icon" />
            <span className="rv-fix-sym-name">{displaySymbolName}</span>
            {displayKind && displayKind !== 'unknown' && (
              <span className="rv-fix-symbol-kind">({displayKind})</span>
            )}
            {issue.line != null && issue.column != null && (
              <span className="rv-fix-symbol-loc">line {issue.line}, col {issue.column}</span>
            )}
          </div>
        )}
        {isMissingSymbol && !displaySymbolName && (
          <div className="rv-fix-inferred-warning">
            <AlertTriangle size={10} />
            Could not infer symbol name — enter it manually in the stub dialog.
          </div>
        )}
        {issue.recommendation && (
          <div className="rv-fix-rec">{issue.recommendation}</div>
        )}
      </div>

      {/* ── RECOMMENDED ── */}
      <div className="rv-fix-section rv-fix-section--recommended">
        <div className="rv-fix-section-hd">
          <span className="rv-fix-section-hd-label">Recommended</span>
        </div>

        {/* allowlisted_auto_fix — deterministic safe patch available */}
        {primaryAction === 'auto_fix' && (
          <div className="rv-fix-cta-card">
            <button
              className="rv-fix-btn rv-fix-btn--cta primary"
              onClick={onApplyFix}
              disabled={resolution === 'pending_patch'}
              title="Apply a safe, deterministic fix"
            >
              <Wrench size={12} />
              {resolution === 'pending_patch' ? 'Fix Recorded ✓' : 'Apply Recommended Fix'}
            </button>
            <span className="rv-fix-cta-note">A safe deterministic fix is available → recorded in workspace on apply</span>
          </div>
        )}

        {/* Missing symbol — Generate Stub (primary) + Mark as External (secondary) */}
        {primaryAction === 'stub_and_external' && (
          <>
            <div className="rv-fix-cta-card">
              <button
                className={`rv-fix-btn rv-fix-btn--cta primary${resolution === 'stubbed' ? ' applied' : ''}`}
                onClick={onGenerateStub}
                disabled={resolution === 'stubbed'}
                title="Generate a compile-only stub so the build can proceed"
              >
                <Puzzle size={12} />
                {resolution === 'stubbed' ? 'Stub Generated ✓' : 'Generate Stub'}
              </button>
              <span className="rv-fix-cta-note">{stubSubnote}</span>
              {resolution === 'stubbed' && (
                <span className="rv-fix-cta-note rv-fix-cta-note--success">
                  Stub in workspace. Click <strong>Re-run Check</strong> to verify.
                </span>
              )}
            </div>
            <div className="rv-fix-cta-card rv-fix-cta-card--secondary">
              <button
                className={`rv-fix-btn rv-fix-btn--external${resolution === 'external' ? ' applied' : ''}`}
                onClick={onMarkExternal}
                disabled={resolution === 'external'}
                title="Record that this symbol belongs to an external library. No code is generated — the requirement is noted for future pom.xml update."
              >
                <Package size={12} />
                {resolution === 'external' ? 'External Dependency Recorded ✓' : 'Mark as External Dependency'}
              </button>
              <span className="rv-fix-cta-note">If this comes from a library jar — no code change; note added for pom.xml</span>
            </div>
            {symbolHint === 'internal' && (
              <div className="rv-fix-symbol-hint rv-fix-symbol-hint--internal">
                <Info size={10} />
                Symbol found in workspace — likely an import or visibility issue, not a missing stub.
              </div>
            )}
            {symbolHint === 'external' && (
              <div className="rv-fix-symbol-hint rv-fix-symbol-hint--external">
                <Package size={10} />
                Index: external namespace detected — "Mark as External" may be appropriate.
              </div>
            )}
          </>
        )}

        {/* Structural error — AI Repair Method */}
        {primaryAction === 'ai_repair_method' && (
          <div className="rv-fix-cta-card">
            <button
              className="rv-fix-btn rv-fix-btn--cta rv-fix-btn-ai-repair"
              onClick={onAIRepairMethod}
              disabled={isWebPortal()}
              title={isWebPortal() ? "Temporarily disabled in QA" : "AI rewrites the full method body surrounding this error — safe for brace/structure issues"}
              style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <Sparkles size={12} />
              AI Repair Method
            </button>
            <span className="rv-fix-cta-note">AI rewrites the broken method with balanced braces → you review before saving</span>
          </div>
        )}

        {/* Type mismatch / general — AI Resolve (Auto) */}
        {primaryAction === 'ai_resolve' && onAIResolveAuto && (
          <div className="rv-fix-cta-card">
            <button
              className="rv-fix-btn rv-fix-btn--cta rv-fix-btn-ai-resolve"
              onClick={onAIResolveAuto}
              disabled={aiResolving || isWebPortal()}
              title={isWebPortal() ? "Temporarily disabled in QA" : "Auto-pick repair strategy, fetch file from S3, apply AI patch, and re-run gate check"}
              style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              {aiResolving ? <Loader2 size={12} className="rv-spin" /> : <Wand2 size={12} />}
              {aiResolving ? 'AI Resolving…' : 'AI Resolve (Auto)'}
            </button>
            <span className="rv-fix-cta-note">AI pinpoints and patches the error, then re-runs the check → written to workspace</span>
          </div>
        )}
      </div>

      {/* ── OTHER OPTIONS ── */}
      <div className="rv-fix-section rv-fix-section--other">
        <button
          className="rv-fix-other-toggle"
          onClick={() => setShowOtherOptions(v => !v)}
        >
          <ChevronRight size={11} className={`rv-fix-chevron${showOtherOptions ? ' open' : ''}`} />
          Other Options
        </button>

        {showOtherOptions && (
          <div className="rv-fix-other-grid">

            {/* Jump to Location — always visible */}
            <button
              className="rv-fix-btn rv-fix-btn--fix-now"
              onClick={onFixNow}
              title="Open the editor at this exact file and line for manual or AI repair"
            >
              <ExternalLink size={12} />
              Jump to Location
            </button>

            {/* Generate Stub — always visible (also in Recommended for missing-symbol) */}
            <button
              className={`rv-fix-btn rv-fix-btn--stub${resolution === 'stubbed' ? ' applied' : ''}`}
              onClick={onGenerateStub}
              disabled={resolution === 'stubbed'}
              title="Generate a compile-only stub so the build can proceed → written to workspace"
            >
              <Puzzle size={12} />
              {resolution === 'stubbed' ? 'Stub Generated ✓' : 'Generate Stub'}
            </button>

            {/* Mark as External — always visible (also in Recommended for missing-symbol) */}
            <button
              className={`rv-fix-btn rv-fix-btn--external${resolution === 'external' ? ' applied' : ''}`}
              onClick={onMarkExternal}
              disabled={resolution === 'external'}
              title="Record that this symbol belongs to an external library — no code generated, adds note for pom.xml"
            >
              <Package size={12} />
              {resolution === 'external' ? 'External Dependency Recorded ✓' : 'Mark as External Dependency'}
            </button>

            {/* AI Resolve (Auto) — always visible (also in Recommended for general errors) */}
            {onAIResolveAuto && (
              <button
                className="rv-fix-btn rv-fix-btn-ai-resolve"
                onClick={onAIResolveAuto}
                disabled={aiResolving || isWebPortal()}
                title={isWebPortal() ? "Temporarily disabled in QA" : "Auto-pick strategy, apply AI patch, re-run check → written to workspace"}
                style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
              >
                {aiResolving ? <Loader2 size={12} className="rv-spin" /> : <Wand2 size={12} />}
                {aiResolving ? 'AI Resolving…' : 'AI Resolve (Auto)'}
              </button>
            )}

            {/* AI Repair Method — always visible (also in Recommended for structural errors) */}
            <button
              className="rv-fix-btn rv-fix-btn-ai-repair"
              onClick={onAIRepairMethod}
              disabled={isWebPortal()}
              title={isWebPortal() ? "Temporarily disabled in QA" : "AI rewrites the enclosing method body — safe for brace/structure issues"}
              style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <Sparkles size={12} />
              AI Repair Method
            </button>

            {/* AI Repair Region — always visible */}
            <button
              className="rv-fix-btn rv-fix-btn-ai-repair"
              onClick={onAIRepairRegion}
              disabled={isWebPortal()}
              title={isWebPortal() ? "Temporarily disabled in QA" : "AI repairs the ±30-line region around the error — use when the method boundary isn't clear"}
              style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <Sparkles size={12} />
              AI Repair Region
            </button>

            {/* AI Repair File Structure — always visible, amber accent for structural issues */}
            <button
              className={`rv-fix-btn${isStructural ? ' rv-fix-btn-ai-repair-file' : ' rv-fix-btn-ai-repair'}`}
              onClick={onAIRepairFileStructure}
              disabled={isWebPortal()}
              title={isWebPortal() ? "Temporarily disabled in QA" : "AI fixes package/import placement and brace balance for the entire file — last resort for severe structural failures"}
              style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <Sparkles size={12} />
              AI Repair File Structure
            </button>

            {/* AI Fix (Safe Patch) — always visible */}
            <button
              className="rv-fix-btn"
              onClick={onAskAI}
              disabled={isWebPortal()}
              title={isWebPortal() ? "Temporarily disabled in QA" : "Open AI suggestion panel for a targeted single-line or custom patch"}
              style={isWebPortal() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <Sparkles size={12} />
              AI Fix (Safe Patch)
            </button>

            {/* Apply as Stub — always visible */}
            <button
              className="rv-fix-btn"
              onClick={onMarkDeferred}
              disabled={resolution === 'deferred'}
              title="Replaces the enclosing method body with a safe TODO stub. Not recommended for file-structure errors."
            >
              <Clock size={12} />
              {resolution === 'deferred' ? 'Applied as Stub ✓' : 'Apply as Stub'}
            </button>

            {/* Comment Out Line — always visible */}
            <button
              className="rv-fix-btn"
              onClick={onCommentOutLine}
              disabled={!issue?.javaLocation?.line}
              title="Replace this line with a // comment → written to workspace"
            >
              <MessageSquare size={12} />
              Comment Out Line
            </button>

            {/* Block Comment — always visible */}
            <button
              className="rv-fix-btn"
              onClick={onBlockCommentSelection}
              title="Wrap selected editor lines with /* */ comment"
            >
              <MessageSquare size={12} />
              Block Comment
            </button>

          </div>
        )}
      </div>

      {/* ── BUILD / GATE ── */}
      <div className="rv-fix-section rv-fix-section--gate">
        <div className="rv-fix-section-hd">
          <span className="rv-fix-section-hd-label">Build</span>
        </div>
        <div className="rv-fix-gate-row">
          <button
            className="rv-fix-btn rv-fix-btn--quick-check"
            onClick={onQuickCheck}
            disabled={quickChecking}
            title="Fast structural check: brace balance, package/import order, file-end brace"
          >
            {quickChecking ? <Loader2 size={12} className="rv-spin" /> : <Activity size={12} />}
            Quick Check
          </button>
          {onStrictCheck && (
            <button
              className="rv-fix-btn"
              onClick={onStrictCheck}
              title="Reload compile results from the server without running Maven"
            >
              <Activity size={12} />
              Strict Check
            </button>
          )}
          <button
            className={`rv-fix-btn rv-fix-btn--rerun${quickCheckResult?.pass === false ? ' rv-fix-btn--warn-rerun' : ''}`}
            onClick={onRerun}
            disabled={rerunning}
            title={quickCheckResult?.pass === false
              ? 'Quick Check found issues — fix them first or re-run anyway'
              : 'Re-run the compile check'
            }
          >
            {rerunning ? <Loader2 size={12} className="rv-spin" /> : <CheckCircle size={12} />}
            Re-run Check{quickCheckResult?.pass === false && ' ⚠'}
          </button>
        </div>

        {quickCheckResult && (
          <div className={`rv-quick-result ${quickCheckResult.pass ? 'pass' : 'fail'}`}>
            {quickCheckResult.pass ? (
              <span><CheckCircle size={10} /> File looks structurally valid</span>
            ) : (
              <ul className="rv-quick-result-errors">
                {quickCheckResult.errors.map((e, i) => (
                  <li key={i}>{e.line ? `L${e.line}: ` : ''}{e.message}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="rv-fix-workspace-note">
          Changes are saved to workspace automatically. Build manually to re-check blockers.
        </div>
      </div>

      {actionMsg && <div className="rv-fix-msg">{actionMsg}</div>}

      <div className="rv-fix-hint">Cmd+Shift+P — Command Palette in the editor</div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ChangeRow — activity log entry (click to navigate)
// ─────────────────────────────────────────────────────────────────────────────

function ChangeRow({ change, onClick }) {
  const icons = {
    fix_applied: <Wrench size={11} className="rv-change-icon fix" />,
    deferred:    <Clock size={11} className="rv-change-icon deferred" />,
    suppressed:  <Check size={11} className="rv-change-icon suppressed" />,
    ai_applied:  <Sparkles size={11} className="rv-change-icon ai" />,
  }
  const labels = {
    fix_applied: 'Fix applied',
    deferred:    'Marked TODO',
    suppressed:  'Suppressed',
    ai_applied:  'AI fix applied',
  }
  const time = change.timestamp
    ? new Date(change.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : ''

  return (
    <button className="rv-change-row" onClick={onClick} title={change.summary}>
      {icons[change.type] || <Check size={11} />}
      <div className="rv-change-body">
        <span className="rv-change-label">{labels[change.type] || change.type}</span>
        <span className="rv-change-file">{change.file}{change.line ? `:${change.line}` : ''}</span>
        <span className="rv-change-summary">{change.summary}</span>
      </div>
      <div className="rv-change-meta">
        {change.fromDB && <span className="rv-change-badge">saved</span>}
        <span className="rv-change-time">{time}</span>
      </div>
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// StructuralPlaybook — collapsible "Manual Fix Playbook" panel
// ─────────────────────────────────────────────────────────────────────────────

function StructuralPlaybook({ open, onToggle, onJumpToEnd, onCheckBraces, braceBalance }) {
  return (
    <div className="rv-playbook">
      <button className="rv-playbook-toggle" onClick={onToggle}>
        <Wrench size={10} />
        Manual Fix Playbook
        <ChevronDown size={12} className={`rv-playbook-chevron${open ? ' open' : ''}`} />
      </button>
      {open && (
        <div className="rv-playbook-body">
          <ol className="rv-playbook-steps">
            <li className="rv-playbook-step">
              <span className="rv-playbook-step-num">1</span>
              <div className="rv-playbook-step-content">
                <span className="rv-playbook-step-label">Check brace balance</span>
                <div className="rv-brace-check">
                  <button className="rv-fix-btn rv-playbook-action-btn" onClick={onCheckBraces}>
                    Check Braces
                  </button>
                  {braceBalance !== null && (
                    <span className={`rv-brace-result ${braceBalance === 0 ? 'balanced' : 'unbalanced'}`}>
                      {braceBalance === 0
                        ? '✓ balanced'
                        : `${braceBalance > 0 ? '+' : ''}${braceBalance} ${braceBalance > 0 ? 'extra {' : 'missing {'}`
                      }
                    </span>
                  )}
                </div>
                <span className="rv-playbook-hint">Must be 0 before compile can pass</span>
              </div>
            </li>
            <li className="rv-playbook-step">
              <span className="rv-playbook-step-num">2</span>
              <div className="rv-playbook-step-content">
                <span className="rv-playbook-step-label">Jump to end of file</span>
                <button className="rv-fix-btn rv-playbook-action-btn" onClick={onJumpToEnd}>
                  <ChevronsDown size={11} /> Jump to End
                </button>
                <span className="rv-playbook-hint">Look for orphaned code after the closing class brace</span>
              </div>
            </li>
            <li className="rv-playbook-step">
              <span className="rv-playbook-step-num">3</span>
              <div className="rv-playbook-step-content">
                <span className="rv-playbook-step-label">AI Repair File Structure</span>
                <span className="rv-playbook-hint">In Fix Panel above — moves package/imports to top and balances braces in one pass</span>
              </div>
            </li>
            <li className="rv-playbook-step">
              <span className="rv-playbook-step-num">4</span>
              <div className="rv-playbook-step-content">
                <span className="rv-playbook-step-label">Save → Re-run Check</span>
                <span className="rv-playbook-hint">Cmd+S to save, then "Re-run Check". Repeat until blockers clear.</span>
              </div>
            </li>
          </ol>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ResolveIssuesView
// ─────────────────────────────────────────────────────────────────────────────

export default function ResolveIssuesView({ analysisContext, onBack, onResumePipeline }) {
  const gateType = analysisContext?.gateType || 'structural'
  const jobId    = analysisContext?.jobId    || null
  const apiBase  = getAPIBaseUrl()

  // Does the context carry real job IDs the editor can use?
  const hasJobData = Boolean(
    analysisContext?.jobs?.codeanalysis_job_id ||
    analysisContext?.jobs?.service_generation_job_id ||
    analysisContext?.jobs?.ingest_job_id ||
    analysisContext?.jobs?.java_packaging_job_id ||
    analysisContext?.jobs?.pre_flight_job_id
  )

  // currentJobId tracks the latest job — updated on re-run so issue list refreshes
  const [currentJobId, setCurrentJobId] = useState(jobId)
  // Captures mount-time jobId so preloadDecisions can guard against cross-job contamination
  const initialJobIdRef = useRef(jobId)
  // [DEBUG/TRACE] Always-current snapshot of currentJobId usable inside stable closures
  // (executeRerun has a stable deps array that excludes currentJobId to prevent stale closures;
  //  using a ref lets us read the live value at call time without adding currentJobId to deps)
  const currentJobIdRef = useRef(currentJobId)
  useEffect(() => { currentJobIdRef.current = currentJobId }, [currentJobId])

  // Mount-only log: confirms which job IDs were passed in from DesignerView.
  // Lives here (after currentJobId state) so we log the initial value correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!import.meta.env.DEV) return
    console.log(' [ResolveIssuesView] mount', {
      currentJobId,
      jobId_prop:             jobId,
      pre_flight_job_id:      analysisContext?.jobs?.pre_flight_job_id,
      java_validation_job_id: analysisContext?.jobs?.java_validation_job_id,
      gateType,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [issues, setIssues]     = useState([])
  const [loading, setLoading]   = useState(true)
  const [selectedIssue, setSelectedIssue] = useState(
    analysisContext?.activeFinding || null
  )

  // Drives CodeEditorView file + line navigation
  const [externalNavTarget, setExternalNavTarget] = useState(
    analysisContext?.activeFinding
      ? {
          cobolFile: analysisContext.activeFinding.cobolLocation?.file || null,
          javaFile:  analysisContext.activeFinding.javaLocation?.file  || null,
          startLine: analysisContext.activeFinding.cobolLocation?.line || null,
        }
      : null
  )

  const [actionMsg,          setActionMsg]          = useState(null)
  const [rerunning,          setRerunning]          = useState(false)
  const [rerunDone,          setRerunDone]          = useState(false)
  const [rerunSummary,       setRerunSummary]       = useState(null)  // {applied, suppressed, skipped}
  const [editorIsDirty,      setEditorIsDirty]      = useState(false)
  const [saving,             setSaving]             = useState(false)
  // ── Build state (status bar + toasts + output drawer) ─────────────────────
  const [buildState,       setBuildState]       = useState({ phase: 'idle', startedAt: null, outputLines: [] })
  const [outputDrawerOpen, setOutputDrawerOpen] = useState(false)
  const [elapsedSecs,      setElapsedSecs]      = useState(0)
  const [toasts,           setToasts]           = useState([])

  // ── Editor tab bar state ──────────────────────────────────────────────────
  // { path, filename, isPreview } — path is the full relative path from explorer
  const [editorTabs,    setEditorTabs]    = useState([])
  const [activeTabPath, setActiveTabPath] = useState(null)
  const [dirtyPaths,    setDirtyPaths]    = useState(new Set())
  // Ref so callbacks always see the latest activeTabPath without stale closures
  const activeTabPathRef = useRef(null)
  activeTabPathRef.current = activeTabPath
  // Ref so the externalNavTarget effect can read latest tabs without stale closure
  const editorTabsRef = useRef([])
  editorTabsRef.current = editorTabs
  const [showSaveRerunModal, setShowSaveRerunModal]  = useState(false)
  const [playbookOpen,             setPlaybookOpen]             = useState(false)
  const [braceBalance,             setBraceBalance]             = useState(null)
  const [quickCheckResult,         setQuickCheckResult]         = useState(null)  // null | {pass, errors}
  const [quickChecking,            setQuickChecking]            = useState(false)
  const [showQuickCheckRerunModal, setShowQuickCheckRerunModal] = useState(false)
  // issuesStale: true after any Monaco-modifying action; cleared when auto-refresh completes.
  // Drives the "Refreshing…" indicator and triggers a background re-run so the issue
  // list reloads with updated line numbers after line-shifting edits.
  const [issuesStale, setIssuesStale] = useState(false)
  // rerunningRef lets early-declared callbacks read current rerunning state without stale closures.
  const rerunningRef = useRef(false)
  rerunningRef.current = rerunning
  // triggerAutoRefreshRef lets callbacks declared before triggerAutoRefresh call it safely.
  const triggerAutoRefreshRef = useRef(null)

  // ── Editor scope: 'focused' (compile_failure_sources) | 'full' (all svcgen/jval Java) ──
  // Always start in Focused mode for fast load — Full Project must be explicitly selected.
  // We intentionally do NOT restore 'full' from localStorage across sessions (it causes
  // long "No files" periods that look broken). Within a session the user can switch freely.
  const [editorScope, setEditorScope] = useState('focused')

  // ── Symbol Index (compile gate only) ─────────────────────────────────────
  const [symbolIndex, setSymbolIndex] = useState(null)
  const [reindexing,  setReindexing]  = useState(false)

  // Upstream job used for full-scope file listing (svcgen or jval job)
  const upstreamJobId = useMemo(() =>
    analysisContext?.jobs?.service_generation_job_id ||
    analysisContext?.jobs?.java_validation_job_id || null,
    [analysisContext]
  )

  const upstreamJobType = useMemo(() =>
    analysisContext?.jobs?.service_generation_job_id ? 'svcgen' : 'jval',
    [analysisContext]
  )

  // ── Symbol Index fetch (compile gate only, non-blocking) ─────────────────
  useEffect(() => {
    if (!currentJobId || gateType !== 'compile') { setSymbolIndex(null); return }
    fetchSymbolIndex(currentJobId, apiBase).then(setSymbolIndex).catch(() => {})
  }, [currentJobId, gateType, apiBase])

  // ── Symbol grouping (useMemo so it recalculates when issues or index change) ─
  const { uniqueSymbolCount } = useMemo(() => {
    if (!symbolIndex) return { uniqueSymbolCount: 0 }
    const missingSymbolIssues = issues.filter(i => i.symbolName)
    return groupIssuesBySymbol(missingSymbolIssues)
  }, [issues, symbolIndex])

  // ── AI Resolve (Auto) hook — single-issue resolve from within Fix & Continue ──
  // Uses a memoized context with currentJobId so the hook sees the latest gate job
  // even if the user has already done one rerun.
  const aiResolveContext = useMemo(() => ({
    ...analysisContext,
    jobId: currentJobId,
  }), [analysisContext, currentJobId])

  const { resolveState, resolveIssue: aiResolveIssue, closeModal: aiCloseModal } = useAIResolve(
    aiResolveContext,
    apiBase,
    gateType,
    () => {}  // no-op; handleAIResolveModalCompleted drives the refresh
  )

  const aiResolving = resolveState.step !== null && resolveState.step !== 'done' && resolveState.step !== 'error'

  // showStructuralBanner: true when the current file has any structural BLOCKER
  const showStructuralBanner = useMemo(() => {
    if (!externalNavTarget?.javaFile) return false
    return issues.some(i =>
      i.javaLocation?.file === externalNavTarget.javaFile &&
      i.severity === 'BLOCK' &&
      isStructuralError(i)
    )
  }, [issues, externalNavTarget])

  const handleScopeChange = useCallback((newScope) => {
    setEditorScope(newScope)
    // Clear tabs on scope switch — prevents stale Focused basename tabs from
    // colliding (as duplicates or stacked UI) with Full Project full-path tabs.
    setEditorTabs([])
    setActiveTabPath(null)
    try { localStorage.setItem(`cev_editor_scope_${gateType}`, newScope) } catch {}
    if (import.meta.env.DEV) {
      console.log('[nav] scope change', { newScope, upstreamJobId })
    }
  }, [gateType, upstreamJobId])

  // ── Sidebar collapse (reuses pattern from CodeEditorView sections panel) ──
  // Allotment key trick forces remount with new defaultSizes so space is reclaimed.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('rv_sidebar_collapsed') === 'true' } catch { return false }
  })

  // Gate job ID for ProjectExplorer (compile or package gate)
  const gateJobIdForExplorer = (
    analysisContext?.jobs?.java_packaging_job_id ||
    analysisContext?.jobs?.pre_flight_job_id ||
    null
  )

  // ── Sidebar tab: 'explorer', 'issues', 'changes', or 'files' ──
  const [sidebarTab, setSidebarTab] = useState('explorer')
  // Increment to force ProjectExplorer to reload its file list from S3
  const [explorerRefreshKey, setExplorerRefreshKey] = useState(0)
  // Per-file expand state in the Files tab (file basename → boolean)
  const [fileGroupsExpanded, setFileGroupsExpanded] = useState({})

  // ── Activity log (in-session changes + hydrated from DB) ──
  const [changesLog, setChangesLog] = useState([])

  // ── AI panel state ──
  const [aiPanelOpen, setAiPanelOpen]       = useState(false)
  const [aiPrompt, setAiPrompt]             = useState('')
  const [aiLoading, setAiLoading]           = useState(false)
  const [aiResult, setAiResult]             = useState(null)
  const [aiError, setAiError]               = useState(null)
  const [aiApplied, setAiApplied]           = useState(false)
  const [aiSaving, setAiSaving]             = useState(false)   // PUT /editor/file in-flight
  const [aiOriginalCode, setAiOriginalCode] = useState('')  // for DiffEditor

  // ── Stub generation from issue row ──
  const [stubIssue,     setStubIssue]     = useState(null)  // issue that triggered stub dialog
  const [stubFqn,       setStubFqn]       = useState('')
  const [stubKind,      setStubKind]      = useState(null)  // inferred kind: 'class'|'field'|'array'|'method'|null
  const [stubFieldType, setStubFieldType] = useState('Object')  // type for field/array stubs
  const [stubWorking,   setStubWorking]   = useState(false)
  // Ref for late-bound callbacks (avoids TDZ — generateStub/recordChange/showMsg declared below)
  const stubCallbacksRef = useRef({})

  const handleStubFromIssue = useCallback((issue) => {
    // Priority order for prefill:
    //   1. issue.symbolName (from Maven "symbol: class Foo" continuation lines — rare in our pipeline)
    //   2. inferMissingSymbol() — extracts token at exact column position from source_lines
    //   3. extractStubFQNHint() — regex fallback on summary text
    let hint = issue?.symbolName || ''
    let kind = issue?.inferredKind || null

    if (!hint) {
      const inferred = inferMissingSymbol(issue)
      if (inferred?.token) {
        hint = inferred.token
        kind = inferred.kind
      }
    }

    if (!hint) {
      hint = extractStubFQNHint(issue?.summary || '')
    }

    setStubFqn(hint)
    setStubKind(kind)
    setStubFieldType('Object')
    setStubIssue(issue)
  }, [])

  const handleStubConfirm = useCallback(async (fqn, fieldType) => {
    if (!gateJobIdForExplorer || !fqn) return
    const issueForStub = stubIssue  // capture before clearing
    const capturedKind = stubKind
    setStubIssue(null)
    setStubKind(null)
    setStubWorking(true)

    const isClassKind = !capturedKind || capturedKind === 'class' || capturedKind === 'unknown'

    try {
      const { generateStub: gs, recordChange: rc, showMsg: sm } = stubCallbacksRef.current

      if (isClassKind) {
        // ── Class stub: create a new .java file in S3 ─────────────────────────
        const path = `src/main/java/${fqn.replace(/\./g, '/')}.java`
        const parts = fqn.split('.')
        const className = parts[parts.length - 1]
        const pkgName = parts.slice(0, -1).join('.')
        const content = `${pkgName ? `package ${pkgName};\n\n` : ''}/**\n * COMPILE-ONLY STUB — generated by RecodeIT\n * Replace with real implementation.\n */\npublic class ${className} {\n    // Stub — no implementation\n}\n`
        await editorCreateFile(gateJobIdForExplorer, path, content, 'focused')
        if (issueForStub?.id && gs) {
          gs(issueForStub.id)
          rc?.('stubbed', issueForStub, `Stub class created: ${fqn}`)
        }
        sm?.(`Stub added to workspace — ${className}.java. Click Build to re-check blockers.`)
        setSidebarTab('explorer')
        setExplorerRefreshKey(k => k + 1)
        setExternalNavTarget({ javaFile: `${className}.java`, cobolFile: null, startLine: null, javaLine: null })
      } else {
        // ── Field / array / method stub: insert into current open file ────────
        const fileContent = editorRef.current?.getFileContent?.() || ''
        if (!fileContent) {
          sm?.('Open the target file in the editor first, then generate the stub.')
          return
        }
        const { stubCode, insertLine } = buildFieldStub(fqn, capturedKind, fieldType || 'Object', fileContent)
        editorRef.current?.insertCodeAtLine(stubCode, insertLine)
        editorRef.current?.triggerSave()
        if (issueForStub?.id && gs) {
          gs(issueForStub.id)
          rc?.('stubbed', issueForStub, `Stub ${capturedKind} inserted: ${fqn}`)
        }
        sm?.(`Stub ${capturedKind} inserted ✓ — click Build to re-check blockers`)
        // Jump editor to the inserted line
        setExternalNavTarget(prev => ({ ...prev, javaLine: insertLine }))
      }
    } catch (err) {
      console.error(' [stub] generate failed:', err)
      stubCallbacksRef.current.showMsg?.(`Stub generation failed: ${err.message || err}`)
    } finally {
      setStubWorking(false)
    }
  }, [gateJobIdForExplorer, stubIssue, stubKind])

  // ── Explain Issue modal state ──
  const [explainOpen,    setExplainOpen]    = useState(false)
  const [explainLoading, setExplainLoading] = useState(false)
  const [explainResult,  setExplainResult]  = useState(null)
  const [explainError,   setExplainError]   = useState(null)

  // Ref to embedded CodeEditorView — exposes triggerAiAction(), insertCodeAtLine()
  const editorRef = useRef(null)

  // Scrollable sidebar tab strip — scroll-into-view on tab change + wheel-to-hscroll + overflow indicator
  const tabsScrollRef = useRef(null)
  const [tabsHasOverflow, setTabsHasOverflow] = useState(false)
  useEffect(() => {
    if (!tabsScrollRef.current) return
    const activeBtn = tabsScrollRef.current.querySelector('.rv-sidebar-tab.active')
    if (activeBtn) activeBtn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [sidebarTab])
  useEffect(() => {
    const el = tabsScrollRef.current
    if (!el) return
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
        el.scrollLeft += e.deltaY
        e.preventDefault()
      }
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    const checkOverflow = () => setTabsHasOverflow(el.scrollWidth > el.clientWidth)
    checkOverflow()
    const ro = new ResizeObserver(checkOverflow)
    ro.observe(el)
    return () => {
      el.removeEventListener('wheel', onWheel)
      ro.disconnect()
    }
  }, [])

  // After re-run, override pre_flight_job_id / java_packaging_job_id so CodeEditorView reloads the new bundle
  const effectiveAnalysisContext = useMemo(() => {
    if (gateType === 'compile' && currentJobId && currentJobId !== jobId) {
      return {
        ...analysisContext,
        jobs: { ...analysisContext?.jobs, pre_flight_job_id: currentJobId },
      }
    }
    if (gateType === 'package' && currentJobId && currentJobId !== jobId) {
      return {
        ...analysisContext,
        jobs: { ...analysisContext?.jobs, java_packaging_job_id: currentJobId },
      }
    }
    return analysisContext
  }, [analysisContext, currentJobId, jobId, gateType])

  const {
    resolutions, applyFix, markDeferred, generateStub, markExternal,
    preloadDecisions, unresolvedCount, clearAllResolutions,
  } = useIssueResolution()

  // When the job changes (after AI Resolve or Re-run), wipe stale resolution badges.
  // Guard: skip on initial mount (initialJobIdRef.current === currentJobId at that point).
  useEffect(() => {
    if (currentJobId === initialJobIdRef.current) return
    if (import.meta.env.DEV)
      console.log(' [ResolveIssuesView] job changed → clearAllResolutions', {
        prevJobId: initialJobIdRef.current, newJobId: currentJobId,
      })
    clearAllResolutions()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJobId])

  // DEV: log resolution counts whenever resolutions or issues change (not per-row)
  useEffect(() => {
    if (!import.meta.env.DEV || !issues.length) return
    const resolved   = Object.keys(resolutions).length
    const unresolved = issues.filter(i => !resolutions[i.id]).length
    console.log(' [ResolveIssuesView] resolution state', {
      currentJobId, resolved, unresolved,
      resolvedIds: Object.entries(resolutions).map(([id, v]) => `${id}:${v}`),
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolutions, issues, currentJobId])

  // Persist sidebar collapsed state
  useEffect(() => {
    try { localStorage.setItem('rv_sidebar_collapsed', String(sidebarCollapsed)) } catch {}
  }, [sidebarCollapsed])

  // ── Load issues ────────────────────────────────────────────────────────────
  // Depends on currentJobId so re-run can refresh by calling setCurrentJobId(newJobId)
  useEffect(() => {
    if (!currentJobId) {
      setIssues([])
      setLoading(false)
      return
    }
    if (import.meta.env.DEV)
      console.log(' [ResolveIssuesView] fetchIssues →', { jobIdUsed: currentJobId, gateType })
    // [DEBUG/TRACE] A) UI — task A.1/A.2: issue list fetch; search "PREFLIGHT_TRACE" to correlate
    console.log('[PREFLIGHT_TRACE]', { action: 'fetch_issues', jobIdUsed: currentJobId, gateType, scope: 'focused' })
    let cancelled = false
    setLoading(true)
    fetchIssues(currentJobId, gateType, apiBase)
      .then(fds => {
        if (cancelled) return
        const normalised = fds.map(f => ({ ...f, state: f.state || 'OPEN' }))
        if (import.meta.env.DEV)
          console.log(' [ResolveIssuesView] fetchIssues ←', {
            jobIdUsed:     currentJobId,
            issueCount:    normalised.length,
            blockersCount: normalised.filter(f => f.severity === 'BLOCK').length,
          })
        setIssues(normalised)

        // Pre-select activeFinding from GateReportView navigation (initial load only)
        if (analysisContext?.activeFinding && currentJobId === jobId) {
          const match = normalised.find(f => f.id === analysisContext.activeFinding.id)
          if (match) setSelectedIssue(match)
        }
        setLoading(false)
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJobId, gateType, apiBase])

  // ── Load existing decisions from DB and pre-populate resolution badges ──────
  const scout_account_id  = analysisContext?.scout_account_id
  const application_name  = analysisContext?.application_name
  const user_id           = analysisContext?.user_id

  useEffect(() => {
    if (!scout_account_id || !application_name || !issues.length || !currentJobId) return
    if (import.meta.env.DEV)
      console.log(' [ResolveIssuesView] preloadDecisions → fetchDecisions', {
        scout_account_id, application_name, gateType, gate_job_id: currentJobId,
      })
    // Pass currentJobId as gate_job_id so the API returns only decisions for this
    // specific compile run — prevents cross-job decision bleed.
    fetchDecisions(scout_account_id, application_name, gateType, currentJobId)
      .then(rows => {
        preloadDecisions(rows, issues)
        if (import.meta.env.DEV)
          console.log(' [ResolveIssuesView] preloadDecisions ← done', {
            gate_job_id: currentJobId, dbRowCount: rows?.length ?? 0, issueCount: issues.length,
          })
        // Hydrate changes log from persisted decisions
        if (rows?.length) {
          const dbChanges = rows.map(d => {
            const matchedIssue = issues.find(i => i.fingerprint === d.fingerprint)
            return {
              id: `db-${d.decision_id || d.fingerprint}`,
              type: d.decision_type === 'CUSTOM_EDIT' ? 'fix_applied'
                  : d.decision_type === 'MARK_TODO_STUB' ? 'deferred'
                  : 'suppressed',
              file: d.target_file || matchedIssue?.javaLocation?.file || 'unknown',
              line: matchedIssue?.javaLocation?.line || null,
              summary: matchedIssue?.summary || d.fingerprint,
              detail: '',
              timestamp: d.updated_at || d.created_at || '',
              issueId: matchedIssue?.id || null,
              fromDB: true,
            }
          }).filter(c => c.issueId)
          setChangesLog(prev => [...prev, ...dbChanges])
        }
      })
      .catch(() => {})  // non-fatal — UI still works without persisted decisions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scout_account_id, application_name, gateType, issues.length, currentJobId])

  // ── Helpers ────────────────────────────────────────────────────────────────
  const showMsg = useCallback((msg, ms = 3500) => {
    setActionMsg(msg)
    setTimeout(() => setActionMsg(null), ms)
  }, [])

  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random()
    setToasts(prev => [...prev, { id, message, type }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4500)
  }, [])

  // Code Checker dispatch — called by CodeEditorView Code Checker buttons
  // Must be declared AFTER showMsg (React TDZ — see memory note #9)
  const handleCodeCheck = useCallback((mode) => {
    if (mode === 'strict') {
      // Strict = read-only re-fetch of compile_result.json for currentJobId.
      // Does NOT trigger a Maven rerun. Does NOT call /gate-decisions/upsert.
      // Clears stale resolution badges first so the authoritative compile state is shown.
      if (!currentJobId) { showMsg('No compile job loaded.'); return }
      if (import.meta.env.DEV)
        console.log(' [ResolveIssuesView] Strict Code Check →', { currentJobId, gateType })
      clearAllResolutions()
      showMsg('Strict check: reloading compile issues…')
      fetchIssues(currentJobId, gateType, apiBase)
        .then(fds => {
          const normalised = fds.map(f => ({ ...f, state: f.state || 'OPEN' }))
          setIssues(normalised)
          if (import.meta.env.DEV)
            console.log(' [ResolveIssuesView] Strict Code Check ←', {
              jobIdUsed:     currentJobId,
              issueCount:    normalised.length,
              blockersCount: normalised.filter(f => f.severity === 'BLOCK').length,
            })
          const blockers = normalised.filter(f => f.severity === 'BLOCK' && f.state === 'OPEN').length
          showMsg(blockers > 0
            ? `Strict check: ${blockers} blocking issue${blockers !== 1 ? 's' : ''} remaining.`
            : 'Strict check: no blockers — compile should pass.')
        })
        .catch(() => showMsg('Strict check failed to load issues.'))
      return
    }
    // 'fast' → run Quick Check on current file
    const content = editorRef.current?.getFileContent?.() || ''
    if (!content) { showMsg('Load a Java file in the editor first'); return }
    setQuickChecking(true)
    setTimeout(() => {
      const result = runQuickCheck(content)
      setQuickCheckResult(result)
      setQuickChecking(false)
      if (import.meta.env.DEV)
        console.log(` [ResolveIssuesView] Code Checker (fast): pass=${result.pass} errors=${result.errors.length}`)
      if (result.pass) showMsg('Code Checker: file looks structurally valid.')
    }, 0)
  }, [showMsg, currentJobId, gateType, apiBase, clearAllResolutions])

  // AI Resolve (Auto) — resolves the currently selected issue via the hook.
  // Must be declared AFTER showMsg (React TDZ — see memory note #9)
  const handleAIResolveAuto = useCallback(() => {
    if (isWebPortal()) return
    if (!selectedIssue) { showMsg('Select an issue first'); return }
    if (import.meta.env.DEV)
      console.log(' [ResolveIssuesView] AI Resolve (Auto) →', { issueId: selectedIssue.id, currentJobId })
    aiResolveIssue(selectedIssue, 'auto')
  }, [selectedIssue, aiResolveIssue, currentJobId, showMsg])

  // Called by AIResolveModal when the rerun finishes.
  // Updating currentJobId triggers the useEffect([currentJobId]) that re-fetches issues.
  // Uses functional setState so we can log prevJobId without needing currentJobId in deps.
  const handleAIResolveModalCompleted = useCallback(({ updatedGateJobId } = {}) => {
    if (!updatedGateJobId) return
    setCurrentJobId(prev => {
      if (import.meta.env.DEV)
        console.log(' [ResolveIssuesView] handleAIResolveModalCompleted', {
          prevJobId:          prev,
          newJobIdFromModal:  updatedGateJobId,
          changed:            prev !== updatedGateJobId,
        })
      return updatedGateJobId
    })
  }, [])

  // ── Activity log recording ─────────────────────────────────────────────────
  const recordChange = useCallback((type, issue, detail = '') => {
    setChangesLog(prev => [{
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type,           // 'fix_applied' | 'deferred' | 'suppressed' | 'ai_applied'
      file: issue?.javaLocation?.file || 'unknown',
      line: issue?.javaLocation?.line || null,
      summary: issue?.summary || '',
      detail,
      timestamp: new Date().toISOString(),
      issueId: issue?.id,
      fromDB: false,
    }, ...prev])
  }, [])

  // Wire up late-bound callbacks for handleStubConfirm (avoids TDZ)
  stubCallbacksRef.current = { generateStub, recordChange, showMsg }

  // ── apiContext — threaded into fix actions for DB persistence ─────────────
  // MUST be declared BEFORE handleGenerateStub / handleMarkExternal
  // (React TDZ: deps arrays are evaluated at const declaration time — see memory note #9)
  const buildApiContext = useCallback((issue) => ({
    account_id:       scout_account_id,
    application_name,
    gate_type:        gateType,
    user_id,
    gate_job_id:      currentJobId,
    issue,
  }), [scout_account_id, application_name, gateType, user_id, currentJobId])

  // ── Canonical tab open / activate ─────────────────────────────────────────────
  // MUST be declared before any useCallback that includes it in a deps array (TDZ guard).
  // Single entry point for all tab creation and editor navigation.
  // Arguments:
  //   basename     — Java filename (e.g. "SomeClass.java"), used by CodeEditorView for file lookup
  //   explicitPath — full relative path from Explorer (preferred as the tab's stable key)
  //   isPreview    — true = italic/replaceable (single-click); false = permanent (double-click)
  //   javaLine     — scroll Java pane to this line after file loads
  //   cobolFile    — optional COBOL pane file (compile issues)
  //   startLine    — optional COBOL pane scroll line
  const openTab = useCallback((basename, {
    explicitPath = null,
    isPreview    = true,
    javaLine     = null,
    cobolFile    = null,
    startLine    = null,
  } = {}) => {
    const path = explicitPath ?? basename

    // Check current tabs for any existing entry for this file.
    // Match by: exact path, same filename, or path suffix (handles "dir/File.java" vs "File.java").
    const existingTabs = editorTabsRef.current
    const existing = existingTabs.find(
      t => t.path === path || t.filename === basename || t.path.endsWith('/' + basename)
    )

    if (existing) {
      if (explicitPath && existing.path !== explicitPath) {
        // Upgrade a bare basename tab to its full path (e.g. Explorer opens a file that was
        // previously opened via issue-click and only had the basename as the key).
        setEditorTabs(prev =>
          prev.map(t => t.path === existing.path ? { ...t, path: explicitPath, isPreview } : t)
        )
        setActiveTabPath(explicitPath)
      } else {
        if (!isPreview && existing.isPreview) {
          // Promote preview → permanent on double-click
          setEditorTabs(prev =>
            prev.map(t => t.path === existing.path ? { ...t, isPreview: false } : t)
          )
        }
        setActiveTabPath(existing.path)
      }
    } else {
      setEditorTabs(prev => {
        // Inner dedup guard — protects against rapid double-fire before re-render
        const alreadyIn = prev.some(
          t => t.path === path || t.filename === basename || t.path.endsWith('/' + basename)
        )
        if (alreadyIn) return prev
        const newTab = { path, filename: basename, isPreview }
        if (isPreview) {
          // VS Code behavior: single-click replaces the current preview slot
          const previewIdx = prev.findIndex(t => t.isPreview)
          if (previewIdx >= 0) return prev.map((t, i) => i === previewIdx ? newTab : t)
        }
        return [...prev, newTab]
      })
      setActiveTabPath(path)
    }

    // Drive CodeEditorView: switch to the file and scroll to line
    setExternalNavTarget({ javaFile: basename, cobolFile, startLine, javaLine })

    if (import.meta.env.DEV) {
      console.log('[tabs] openTab', { basename, path, javaLine, isPreview, hadExisting: !!existing })
    }
  }, [])  // stable — uses editorTabsRef and stable state setters, no closure capture needed

  // ── Missing Symbol actions ─────────────────────────────────────────────────

  // "Fix Now" — re-triggers editor navigation to the exact error location
  const handleFixNow = useCallback(() => {
    if (!selectedIssue?.javaLocation) { showMsg('No file location for this issue.'); return }
    const basename = selectedIssue.javaLocation.file.split('/').pop()
    openTab(basename, { javaLine: selectedIssue.javaLocation.line })
    if (import.meta.env.DEV)
      console.log('[nav] Fix Now', { file: basename, line: selectedIssue.javaLocation.line })
  }, [selectedIssue, openTab, showMsg])

  // "Generate Stub" — opens dialog pre-filled with FQN, creates file immediately in S3
  const handleGenerateStub = useCallback(() => {
    if (!selectedIssue) return
    if (import.meta.env.DEV)
      console.log(' [ResolveIssuesView] Generate Stub → showing dialog', {
        issueId: selectedIssue.id,
        symbolName: selectedIssue.symbolName,
        symbolCategory: selectedIssue.symbolCategory,
      })
    // Reuse the same stub dialog + immediate S3 creation path as issue-row stubs
    handleStubFromIssue(selectedIssue)
  }, [selectedIssue, handleStubFromIssue])

  // "Mark as External Dependency" (advanced)
  const handleMarkExternal = useCallback(() => {
    if (!selectedIssue) return
    if (import.meta.env.DEV)
      console.log(' [ResolveIssuesView] Mark External →', { issueId: selectedIssue.id })
    markExternal(selectedIssue.id, buildApiContext(selectedIssue), {
      symbol: selectedIssue.symbolName || null,
    })
    recordChange('suppressed', selectedIssue, 'external dependency recorded')
    showMsg('External dependency recorded — add the Maven artifact to pom.xml manually.')
  }, [selectedIssue, markExternal, buildApiContext, recordChange, showMsg])

  // "Re-index" — rebuild symbol index from current workspace files (non-fatal)
  const handleReindex = useCallback(async () => {
    if (!currentJobId || reindexing) return
    setReindexing(true)
    try {
      await rebuildSymbolIndex(currentJobId, apiBase)
      const fresh = await fetchSymbolIndex(currentJobId, apiBase)
      setSymbolIndex(fresh)
      showMsg('Symbol index rebuilt.')
    } catch (err) {
      showMsg(`Re-index failed: ${err?.message || 'unknown error'}`)
    } finally {
      setReindexing(false)
    }
  }, [currentJobId, reindexing, apiBase, showMsg])

  const handleChangeClick = useCallback((change) => {
    if (change.file && change.line) {
      openTab(change.file.split('/').pop(), { javaLine: change.line })
    }
    if (change.issueId) {
      const issue = issues.find(i => i.id === change.issueId)
      if (issue) setSelectedIssue(issue)
    }
  }, [issues, openTab])

  // ── Issue selection ────────────────────────────────────────────────────────
  const handleSelectIssue = useCallback((issue) => {
    setSelectedIssue(issue)

    // Anchor-based navigation: after any line-shifting edit (Mark TODO, Comment Out,
    // Block Comment, AI Apply Fix), javaLocation.line may be stale. Search for the
    // original error text (issue.snippet) in the live editor content to find where
    // the line actually is now. Falls back to javaLocation.line gracefully.
    let javaLine = issue.javaLocation?.line || null
    const fileContent = editorRef.current?.getFileContent?.() || ''
    const fileTotalLines = fileContent ? fileContent.split('\n').length : Infinity

    if (javaLine && issue.snippet?.trim()) {
      if (fileContent) {
        const anchoredLine = findAnchorLine(fileContent, javaLine, issue.snippet.trim())
        if (anchoredLine !== javaLine) {
          console.log(` [anchor] "${issue.summary.slice(0, 50)}" line ${javaLine} → ${anchoredLine} (file=${issue.javaLocation?.file} snippetLen=${issue.snippet.length})`)
          javaLine = anchoredLine
        }
      }
    }

    // Clamp to file bounds — prevents Monaco from scrolling to EOF when statedLine > totalLines
    if (javaLine && javaLine > fileTotalLines) {
      console.log(` [anchor] clamped javaLine ${javaLine} → ${fileTotalLines} (EOF guard, file=${issue.javaLocation?.file})`)
      javaLine = fileTotalLines
    }

    const javaFile = issue.javaLocation?.file || null
    if (javaFile) {
      openTab(javaFile.split('/').pop(), {
        javaLine,
        cobolFile: issue.cobolLocation?.file || null,
        startLine: issue.cobolLocation?.line || null,
      })
    } else if (issue.cobolLocation?.file) {
      // COBOL-only issues — just navigate the source pane (no Java tab to open)
      setExternalNavTarget({
        cobolFile: issue.cobolLocation.file,
        javaFile:  null,
        startLine: issue.cobolLocation.line || null,
        javaLine:  null,
      })
    }
  }, [openTab])

  // ── ProjectExplorer: single-click → preview tab ────────────────────────────
  const handleExplorerFileOpen = useCallback((item) => {
    openTab(item.path.split('/').pop(), { explicitPath: item.path, isPreview: true })
    if (import.meta.env.DEV) console.log('[tabs] openTab source=explorer path=', item.path)
  }, [openTab])

  // ── ProjectExplorer: double-click → permanent tab ──────────────────────────
  const handleExplorerFileDoubleClick = useCallback((item) => {
    openTab(item.path.split('/').pop(), { explicitPath: item.path, isPreview: false })
    if (import.meta.env.DEV) console.log('[tabs] openTab source=explorer-dbl path=', item.path)
  }, [openTab])

  // ── Tab bar: activate a tab ────────────────────────────────────────────────
  const handleTabActivate = useCallback((path) => {
    const tab = editorTabsRef.current.find(t => t.path === path)
    if (import.meta.env.DEV) console.log('[tabs] activate', { path, filename: tab?.filename, found: !!tab })
    if (!tab) return
    setActiveTabPath(path)
    setExternalNavTarget({ javaFile: tab.filename, cobolFile: null, startLine: null, javaLine: null })
  }, [])  // stable — uses editorTabsRef

  // ── Tab bar: close a tab ──────────────────────────────────────────────────
  const handleTabClose = useCallback((path) => {
    if (dirtyPaths.has(path)) {
      const filename = path.split('/').pop()
      if (!window.confirm(`Discard changes to ${filename}?`)) return
    }
    const remaining = editorTabs.filter(t => t.path !== path)
    setEditorTabs(remaining)
    setDirtyPaths(prev => { const next = new Set(prev); next.delete(path); return next })
    if (activeTabPath === path) {
      if (remaining.length > 0) {
        const idx = editorTabs.findIndex(t => t.path === path)
        const nearestIdx = Math.max(0, Math.min(idx, remaining.length - 1))
        const nearest = remaining[nearestIdx]
        setActiveTabPath(nearest.path)
        setExternalNavTarget({ javaFile: nearest.filename, cobolFile: null, startLine: null, javaLine: null })
      } else {
        setActiveTabPath(null)
      }
    }
  }, [dirtyPaths, editorTabs, activeTabPath])

  // ── Dirty change: track per-tab dirty state ────────────────────────────────
  const handleDirtyChange = useCallback((isDirty) => {
    setEditorIsDirty(isDirty)
    const path = activeTabPathRef.current
    if (!path) return
    setDirtyPaths(prev => {
      const next = new Set(prev)
      if (isDirty) next.add(path)
      else next.delete(path)
      return next
    })
  }, [])

  // ── Fix Panel actions ──────────────────────────────────────────────────────
  const handleApplyFix = useCallback(() => {
    if (!selectedIssue) return
    applyFix(selectedIssue.id, { content: '' }, buildApiContext(selectedIssue))
    recordChange('fix_applied', selectedIssue)
    showMsg('Fix recorded — pending save.')
  }, [selectedIssue, applyFix, buildApiContext, showMsg, recordChange])

  // ── Structure-aware stub — shared by handleMarkDeferred + handleApplyAsStub ─
  // Developer note: Strategy 1 detects the enclosing Java method via brace tracking
  // and replaces the ENTIRE body with a minimal-compiling stub (keeps signature,
  // adds /* TODO */ + default return). This prevents "unreachable code", "missing
  // return", and brace-mismatch errors that a naive one-line replacement causes.
  // Falls back to Strategy 2 (single-line TODO) when method detection fails.
  const applyStructureAwareStub = useCallback((issue) => {
    const line    = issue.javaLocation?.line
    const file    = issue.javaLocation?.file || 'unknown'
    const summary = issue.summary || 'manual resolution required'
    console.log(` [Stub] gateType=${gateType} file=${file} line=${line} fp=${issue.fingerprint}`)

    let originalLine = '', contextBefore = [], contextAfter = [], editorReady = false
    if (editorRef.current?.getCodeAroundLine && line) {
      const exactCtx = editorRef.current.getCodeAroundLine(line, 0)
      if (exactCtx?.text !== undefined) {
        editorReady  = true
        originalLine = (exactCtx.text || '').trim()
      }
      const wideCtx = editorRef.current.getCodeAroundLine(line, 3)
      if (wideCtx?.text) {
        const allLines = wideCtx.text.split('\n')
        const offset   = line - wideCtx.startLine
        contextBefore  = allLines.slice(0, offset).map(l => l.trim()).filter(Boolean)
        contextAfter   = allLines.slice(offset + 1).map(l => l.trim()).filter(Boolean)
      }
    }

    // Strategy 1: method-level stub (replaces whole method body)
    if (editorReady && line) {
      const fileContent = editorRef.current?.getFileContent?.() || ''
      if (fileContent) {
        const method = findEnclosingMethod(fileContent, line)
        if (method) {
          const stub = buildMethodStub(method, summary)
          console.log(` [Stub] Strategy 1: method stub L${method.startLine}–${method.endLine} returnType=${method.returnType}`)
          applyFix(issue.id, {
            start_line: method.startLine, end_line: method.endLine,
            replacement_text: stub, original_line: originalLine,
            context_before: contextBefore, context_after: contextAfter,
          }, buildApiContext(issue))
          editorRef.current.replaceLineRange(method.startLine, method.endLine, stub)
          recordChange('deferred', issue, `Method stub L${method.startLine}–${method.endLine}`)
          showMsg(`Method body stubbed (${method.endLine - method.startLine + 1} lines replaced) — decision saved.`)
          triggerAutoRefreshRef.current?.()
          return
        }
      }
    }

    // Strategy 2: single-line TODO fallback (original behavior)
    const commentLines = [`        // TODO: Fix compile issue: ${summary}`]
    if (originalLine) commentLines.push(`        // ORIGINAL: ${originalLine}`)
    let comment = commentLines.join('\n')
    comment = comment.replace(/\bunknown\s*\([^)]*\)\s*;?/g, '/* removed unknown() */')
    console.log(` [Stub] Strategy 2: single-line fallback line=${line}`)
    applyFix(issue.id, {
      start_line: line, end_line: line, replacement_text: comment,
      original_line: originalLine, context_before: contextBefore, context_after: contextAfter,
    }, buildApiContext(issue))
    if (editorReady && editorRef.current?.insertCodeAtLine && line) {
      editorRef.current.insertCodeAtLine(comment, line)
      recordChange('deferred', issue, 'compile-safe TODO replacement')
      showMsg('Failing line replaced with TODO comment — decision saved.')
    } else {
      console.warn(` [Stub] editor not ready — decision saved to DB but line not patched.`)
      recordChange('deferred', issue, 'TODO decision saved (load file to see change)')
      showMsg('Decision saved — open the file in the editor to see the change.')
    }
    triggerAutoRefreshRef.current?.()
  }, [gateType, applyFix, buildApiContext, showMsg, recordChange])

  const handleMarkDeferred = useCallback(() => {
    if (!selectedIssue) return
    if (gateType === 'compile' || gateType === 'package') {
      applyStructureAwareStub(selectedIssue)
    } else {
      markDeferred(selectedIssue.id, buildApiContext(selectedIssue))
      recordChange('deferred', selectedIssue)
      showMsg('Marked as TODO — decision saved.')
    }
  }, [selectedIssue, gateType, markDeferred, applyStructureAwareStub, buildApiContext, showMsg, recordChange])

  // ── "Apply as Stub" from AI modal — ignore AI patch, apply structure-aware stub ──
  const handleApplyAsStub = useCallback(() => {
    if (!selectedIssue) return
    setAiApplied(true)   // disable Apply buttons in modal (action taken)
    applyStructureAwareStub(selectedIssue)
  }, [selectedIssue, applyStructureAwareStub])

  // ── Explain Issue — calls AI for plain-English explanation (no JSON patch) ──
  const handleExplainIssue = useCallback(async () => {
    if (!selectedIssue) return
    const fileName = selectedIssue.javaLocation?.file || 'unknown'
    const snippet  = aiOriginalCode || selectedIssue.snippet || ''
    const prompt   = [
      `You are a COBOL-to-Java migration expert. Explain the following Java compile error in plain English.`,
      `Provide exactly 3 sections:`,
      `1. What this error means`,
      `2. Likely cause (COBOL-to-Java translation issue)`,
      `3. Recommended fix action`,
      ``,
      `Error: ${selectedIssue.summary}`,
      `File: ${fileName}${selectedIssue.javaLocation?.line ? ':' + selectedIssue.javaLocation.line : ''}`,
      snippet ? `\nCode context:\n${snippet}` : '',
    ].filter(Boolean).join('\n')

    setExplainResult(null)
    setExplainError(null)
    setExplainLoading(true)
    setExplainOpen(true)

    try {
      const res = await customPromptAI(snippet, prompt, selectedIssue.id, fileName, {
        gate_type:     gateType,
        issue_summary: selectedIssue.summary,
      })
      setExplainResult(res)
    } catch (err) {
      setExplainError(err.message || 'AI request failed')
    } finally {
      setExplainLoading(false)
    }
  }, [selectedIssue, aiOriginalCode, gateType])

  // How many distinct Java file names appear across all issues (used for single-file hint)
  const distinctJavaFiles = useMemo(() => {
    const files = new Set(issues.map(i => i.javaLocation?.file).filter(Boolean))
    return files.size
  }, [issues])

  // ── Files tab: group issues by Java file basename ──────────────────────────
  const fileGroups = useMemo(() => {
    const map = new Map()
    for (const issue of issues) {
      const rawFile = issue.javaLocation?.file || '(unknown)'
      const file = rawFile.split('/').pop()
      if (!map.has(file)) map.set(file, [])
      map.get(file).push(issue)
    }
    // Sort: files with blockers first, then by issue count desc
    return [...map.entries()]
      .map(([file, fileIssues]) => ({
        file,
        issues: fileIssues,
        blockers: fileIssues.filter(i => i.severity === 'BLOCK').length,
        warnings: fileIssues.filter(i => i.severity !== 'BLOCK').length,
      }))
      .sort((a, b) => b.blockers - a.blockers || b.issues.length - a.issues.length)
  }, [issues])

  // ── AI Repair Method: rewrites the enclosing method body (safe for brace/structure errors) ──
  const handleAIRepairMethod = useCallback(() => {
    if (isWebPortal()) return
    if (!selectedIssue) { showMsg('Select an issue first'); return }
    const line    = selectedIssue.javaLocation?.line || 1
    const file    = selectedIssue.javaLocation?.file || 'unknown.java'
    const summary = selectedIssue.summary || ''

    const fileContent = editorRef.current?.getFileContent?.() || ''
    let startLine = Math.max(1, line - 20), endLine = line + 20
    let methodCode = ''
    if (fileContent) {
      const method = findEnclosingMethod(fileContent, line)
      if (method) {
        startLine  = method.startLine
        endLine    = method.endLine
        methodCode = fileContent.split('\n').slice(method.startLine - 1, method.endLine).join('\n')
      } else {
        const lns = fileContent.split('\n')
        endLine    = Math.min(lns.length, endLine)
        methodCode = lns.slice(startLine - 1, endLine).join('\n')
      }
    } else {
      methodCode = selectedIssue.snippet || '(file not loaded in editor yet)'
    }

    const prompt = [
      `You MUST respond with ONLY valid JSON and absolutely no other text.`,
      ``,
      `Repair the broken Java method in file "${file}".`,
      `The method may have: brace mismatches, illegal statements, or misplaced import/package lines.`,
      `Rewrite it with balanced braces and correct Java syntax. Keep the method signature intact.`,
      `Return exactly this JSON:`,
      `{`,
      `  "action": "CUSTOM_EDIT",`,
      `  "file": "${file}",`,
      `  "start_line": ${startLine},`,
      `  "end_line": ${endLine},`,
      `  "replacement_text": "<corrected method code with balanced braces>",`,
      `  "confidence": 0.85,`,
      `  "explanation": "<one sentence describing what was repaired>"`,
      `}`,
      ``,
      `Compile error at line ${line}: ${summary}`,
      `Method code (lines ${startLine}–${endLine}):`,
      '```java',
      methodCode.slice(0, 4000),
      '```',
    ].join('\n')

    setAiPrompt(prompt)
    setAiOriginalCode(methodCode.slice(0, 4000))
    setAiResult(null); setAiError(null); setAiApplied(false)
    setAiPanelOpen(true)
    console.log(` [ResolveIssuesView] AI Repair Method: L${startLine}–${endLine} file=${file}`)
  }, [selectedIssue, showMsg])

  // ── AI Repair Region: rewrites ±30 lines around the error ──
  const handleAIRepairRegion = useCallback(() => {
    if (isWebPortal()) return
    if (!selectedIssue) { showMsg('Select an issue first'); return }
    const line    = selectedIssue.javaLocation?.line || 1
    const file    = selectedIssue.javaLocation?.file || 'unknown.java'
    const summary = selectedIssue.summary || ''

    const fileContent = editorRef.current?.getFileContent?.() || ''
    const lns = fileContent ? fileContent.split('\n') : []
    const startLine   = Math.max(1, line - 30)
    const endLine     = Math.min(lns.length || (line + 30), line + 30)
    const regionCode  = lns.length
      ? lns.slice(startLine - 1, endLine).join('\n')
      : (selectedIssue.snippet || '(file not loaded in editor yet)')

    const prompt = [
      `You MUST respond with ONLY valid JSON and absolutely no other text.`,
      ``,
      `Fix the Java compile error in file "${file}" at line ${line}.`,
      `The region of code around the error may have brace imbalance or illegal statements.`,
      `Repair the region so it compiles correctly. Do not change code beyond what is necessary.`,
      `Return exactly this JSON:`,
      `{`,
      `  "action": "CUSTOM_EDIT",`,
      `  "file": "${file}",`,
      `  "start_line": ${startLine},`,
      `  "end_line": ${endLine},`,
      `  "replacement_text": "<corrected Java code for this region>",`,
      `  "confidence": 0.8,`,
      `  "explanation": "<one sentence describing what was fixed>"`,
      `}`,
      ``,
      `Compile error at line ${line}: ${summary}`,
      `Region (lines ${startLine}–${endLine}):`,
      '```java',
      regionCode.slice(0, 4000),
      '```',
    ].join('\n')

    setAiPrompt(prompt)
    setAiOriginalCode(regionCode.slice(0, 4000))
    setAiResult(null); setAiError(null); setAiApplied(false)
    setAiPanelOpen(true)
    console.log(` [ResolveIssuesView] AI Repair Region: L${startLine}–${endLine} file=${file}`)
  }, [selectedIssue, showMsg])

  // ── AI Repair File Structure: whole-file repair for structural errors ──
  // Sends entire file to Claude with instructions to move package/imports to top,
  // balance braces, and fix class declaration.  Use for cascading structural errors.
  const handleAIRepairFileStructure = useCallback(() => {
    if (isWebPortal()) return
    if (!selectedIssue) { showMsg('Select an issue first'); return }
    const file    = selectedIssue.javaLocation?.file || 'unknown.java'
    const line    = selectedIssue.javaLocation?.line || 1
    const summary = selectedIssue.summary || ''

    const fileContent = editorRef.current?.getFileContent?.() || ''
    if (!fileContent) {
      showMsg('Load the Java file in the editor first — then run File Structure repair.')
      return
    }
    const totalLines    = fileContent.split('\n').length
    const contentForAI  = fileContent.slice(0, 8000)
    const truncated     = fileContent.length > 8000

    const prompt = [
      `You MUST respond with ONLY valid JSON and absolutely no other text.`,
      ``,
      `This Java file "${file}" has structural problems: package/import lines may be inside`,
      `method bodies, the class declaration may be missing or malformed, or braces may be unbalanced.`,
      ``,
      `Repair the ENTIRE file structure by:`,
      `1. Moving the package statement to line 1 (if present).`,
      `2. Placing all import statements immediately after the package line.`,
      `3. Ensuring the class declaration follows imports.`,
      `4. Balancing all braces — every { must have a matching }.`,
      `5. Removing any package/import lines that appear inside method bodies.`,
      `6. Do NOT change method logic — only fix file structure.`,
      ``,
      `Return exactly this JSON (replacement_text = the entire corrected file):`,
      `{`,
      `  "action": "CUSTOM_EDIT",`,
      `  "file": "${file}",`,
      `  "start_line": 1,`,
      `  "end_line": ${totalLines},`,
      `  "replacement_text": "<entire corrected Java file content>",`,
      `  "confidence": 0.75,`,
      `  "explanation": "<one sentence summarizing the structural repairs made>"`,
      `}`,
      ``,
      `Trigger error at line ${line}: ${summary}`,
      truncated ? `(File content truncated to 8 000 chars for context)` : '',
      ``,
      `File content:`,
      '```java',
      contentForAI,
      '```',
    ].filter(Boolean).join('\n')

    setAiPrompt(prompt)
    setAiOriginalCode(contentForAI)
    setAiResult(null); setAiError(null); setAiApplied(false)
    setAiPanelOpen(true)
    console.log(` [ResolveIssuesView] AI Repair File Structure: ${totalLines} lines truncated=${truncated}`)
  }, [selectedIssue, showMsg])

  // ── Ask AI: opens the AI suggestion modal ────────────────────────────────
  const handleAskAI = useCallback(() => {
    if (isWebPortal()) return
    if (!selectedIssue) {
      showMsg('Select an issue first')
      return
    }

    const file = selectedIssue.javaLocation?.file || 'unknown file'
    const line = selectedIssue.javaLocation?.line || '?'

    // JSON-contract prompt: Claude must return ONLY the structured patch JSON.
    // The modal parser tries JSON.parse() on the response; shows a parse-error
    // UI if it fails. Users can regenerate or adjust the prompt.
    const defaultPrompt = [
      `You MUST respond with ONLY valid JSON and absolutely no other text.`,
      ``,
      `Fix the Java ${(gateType === 'compile' || gateType === 'package') ? 'compile error' : 'issue'} below.`,
      `Return exactly this JSON structure:`,
      `{`,
      `  "action": "CUSTOM_EDIT",`,
      `  "file": "${file}",`,
      `  "start_line": ${line},`,
      `  "end_line": ${line},`,
      `  "replacement_text": "<corrected Java code for that line or block>",`,
      `  "confidence": 0.95,`,
      `  "explanation": "<one sentence describing the fix>"`,
      `}`,
      ``,
      `Error: ${selectedIssue.summary}`,
      `File: ${file}, Line: ${line}`,
      selectedIssue.recommendation ? `Recommendation: ${selectedIssue.recommendation}` : '',
      selectedIssue.snippet ? `\nCode context:\n${selectedIssue.snippet}` : '',
    ].filter(Boolean).join('\n')

    // Capture original code around the issue line (for DiffEditor in modal)
    let origCode = selectedIssue.snippet || ''
    if (editorRef.current?.getCodeAroundLine && selectedIssue.javaLocation?.line) {
      const ctx = editorRef.current.getCodeAroundLine(selectedIssue.javaLocation.line, 8)
      if (ctx?.text) origCode = ctx.text
    }

    // Reset AI state and open modal
    setAiPrompt(defaultPrompt)
    setAiOriginalCode(origCode)
    setAiResult(null)
    setAiError(null)
    setAiApplied(false)
    setAiPanelOpen(true)
    console.log(' [ResolveIssuesView] AI modal opened for issue', { id: selectedIssue.id, fingerprint: selectedIssue.fingerprint })
  }, [selectedIssue, gateType, showMsg])

  // ── AI panel handlers ────────────────────────────────────────────────────
  const handleAIGenerate = useCallback(async () => {
    if (!aiPrompt.trim() || !selectedIssue) return

    setAiLoading(true)
    setAiResult(null)
    setAiError(null)
    setAiApplied(false)

    const fileName = selectedIssue.javaLocation?.file || 'Fix & Continue'
    console.log(' [ResolveIssuesView] AI generate', {
      issueId: selectedIssue.id,
      fingerprint: selectedIssue.fingerprint,
      prompt: aiPrompt.slice(0, 80),
    })

    try {
      // Use live editor code if available, else fall back to issue snippet
      const codeContext = aiOriginalCode || selectedIssue.snippet || ''
      const result = await customPromptAI(
        codeContext,
        aiPrompt,
        selectedIssue.id,
        fileName,
        {
          gate_type: gateType,
          issue_summary: selectedIssue.summary,
          file: selectedIssue.javaLocation?.file,
          line: selectedIssue.javaLocation?.line,
        }
      )
      setAiResult(result)
    } catch (err) {
      console.error('[ResolveIssuesView] AI suggestion error:', err)
      setAiError(err.message || 'AI request failed')
    } finally {
      setAiLoading(false)
    }
  }, [aiPrompt, selectedIssue, gateType, aiOriginalCode])

  // handleAIApply receives a parsed patch JSON object from AISuggestionModal.
  // Uses replaceLineRange for multi-line patches, insertCodeAtLine for single-line.
  const handleAIApply = useCallback((patchObj) => {
    if (!selectedIssue || !patchObj) return

    const startLine = patchObj.start_line || selectedIssue.javaLocation?.line || 1
    const endLine   = patchObj.end_line   || startLine
    // Safety-net: strip unknown() calls that would be compile blockers
    let text = (patchObj.replacement_text || '')
      .replace(/\bunknown\s*\([^)]*\)\s*;?/g, '/* removed unknown() */')

    console.log(' [ResolveIssuesView] AI apply (structured patch)', {
      issueId: selectedIssue.id,
      fingerprint: selectedIssue.fingerprint,
      startLine, endLine,
      codeLenChars: text.length,
      confidence: patchObj.confidence,
    })

    // Capture anchoring context for line-drift-safe DecisionApplier matching
    let originalLine = '', contextBefore = [], contextAfter = []
    if (editorRef.current?.getCodeAroundLine && startLine) {
      const exactCtx = editorRef.current.getCodeAroundLine(startLine, 0)
      if (exactCtx?.text) originalLine = exactCtx.text.trim()
      const wideCtx = editorRef.current.getCodeAroundLine(startLine, 3)
      if (wideCtx?.text) {
        const allLines = wideCtx.text.split('\n')
        const offset = startLine - wideCtx.startLine
        contextBefore = allLines.slice(0, offset).map(l => l.trim()).filter(Boolean)
        contextAfter  = allLines.slice(offset + 1).map(l => l.trim()).filter(Boolean)
      }
    }

    // Persist as CUSTOM_EDIT GateDecision — DecisionApplier applies it on rerun
    applyFix(selectedIssue.id, {
      start_line:       startLine,
      end_line:         endLine,
      replacement_text: text,
      original_line:    originalLine,
      context_before:   contextBefore,
      context_after:    contextAfter,
    }, buildApiContext(selectedIssue))

    // Apply to Monaco editor — replaceLineRange for multi-line, insertCodeAtLine for single
    if (endLine > startLine && editorRef.current?.replaceLineRange) {
      editorRef.current.replaceLineRange(startLine, endLine, text)
    } else if (editorRef.current?.insertCodeAtLine) {
      editorRef.current.insertCodeAtLine(text, startLine)
    }

    recordChange('ai_applied', selectedIssue, patchObj.explanation || 'AI-generated fix')
    setAiApplied(true)
    showMsg('AI fix applied and decision saved.')
    // Monaco was modified — mark issues stale so line numbers auto-refresh
    triggerAutoRefreshRef.current?.()
    // Background save — write S3 overlay so dirty flag clears and editor shows patch on reload.
    // Fire-and-forget; failure is logged but does not block the user.
    setTimeout(() => {
      editorRef.current?.triggerSave?.()?.catch(err =>
        console.warn(' [AIApply] Background save failed (non-blocking):', err?.message)
      )
    }, 60)
  }, [selectedIssue, applyFix, buildApiContext, showMsg, recordChange])

  // ── Re-run ───────────────────────────────────────────────────────────────
  // executeRerun contains the actual gate-rerun logic (renamed from handleRerun).
  // handleRerun wraps it with a dirty-state check — shows modal if editor has unsaved changes.
  const executeRerun = useCallback(async () => {
    const { scout_account_id, application_name, user_id } = analysisContext || {}
    if (!scout_account_id || !application_name || !user_id) {
      showMsg('Cannot re-run: account/app context missing — run from the workflow first')
      return
    }

    const _buildStart = Date.now()
    setBuildState({
      phase: 'running',
      startedAt: _buildStart,
      outputLines: [`[${new Date(_buildStart).toLocaleTimeString()}] Build started — ${GATE_LABELS[gateType] ?? gateType}`],
    })
    addToast('Build started')
    setRerunning(true)
    showMsg('Submitting re-run job…')

    try {
      // ── Package gate: POST /rerun-node with node_id='java_packaging' ──
      if (gateType === 'package') {
        const jpkgJobId = analysisContext?.jobs?.java_packaging_job_id
        console.log(` [ResolveIssuesView] Re-run Check (package): java_packaging_job_id=${jpkgJobId} account=${scout_account_id} app=${application_name}`)
        const res = await fetch(`${apiBase}/rerun-node`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            scout_account_id,
            application_name,
            user_id,
            node_id: 'java_packaging',
          }),
        })
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}))
          throw new Error(errBody.detail || `API error: ${res.status}`)
        }
        const { job_id: newJobId } = await res.json()
        showMsg(`Job ${newJobId} queued — polling…`)
        setBuildState(prev => ({ ...prev, outputLines: [...prev.outputLines, `  Job ${newJobId} submitted — awaiting results...`] }))

        // Poll GET /jobs/{newJobId} (2 s interval, 9 min max)
        let passed = false
        for (let i = 0; i < 270; i++) {
          await new Promise(r => setTimeout(r, 2000))
          try {
            const jr = await fetch(`${apiBase}/jobs/${newJobId}`, { headers: getAuthHeaders() })
            if (!jr.ok) continue
            const job = await jr.json()
            const norm = (job.status || '').toLowerCase()
            if (['completed', 'succeeded', 'failed'].includes(norm)) {
              passed = norm === 'completed' || norm === 'succeeded'
              break
            }
          } catch (_) { /* transient network error — retry */ }
        }

        setCurrentJobId(newJobId)
        console.log(` [ResolveIssuesView] Re-run Check (package): job=${newJobId} passed=${passed}`)
        if (passed) {
          const elapsed = Math.round((Date.now() - _buildStart) / 1000)
          setBuildState(prev => ({ ...prev, phase: 'success', outputLines: [...prev.outputLines, `[${new Date().toLocaleTimeString()}] PASSED (${elapsed}s)`] }))
          addToast('Build completed successfully', 'success')
          setRerunDone(true)
          showMsg('Package compile passed — Resume Pipeline now enabled', 5000)
        } else {
          const elapsed = Math.round((Date.now() - _buildStart) / 1000)
          setBuildState(prev => ({ ...prev, phase: 'failed', outputLines: [...prev.outputLines, `[${new Date().toLocaleTimeString()}] FAILED (${elapsed}s)`, `> Fix an issue in the Fix Panel, then click Build again.`] }))
          addToast('Build completed with errors', 'error')
          showMsg('Package compile still failing — review the updated issue list', 5000)
        }
        return
      }

      // ── Structural / Compile gates: existing dedicated start endpoints ──
      const endpoint = gateType === 'structural'
        ? `${apiBase}/java-validation/start`
        : `${apiBase}/pre-flight/start`

      // [DEBUG/TRACE] A) UI — task A.1/A.2 + D) Guardrail D.1:
      // Read currentJobId via ref (stable at call time — avoids stale closure).
      // For compile gate: pass as overlay_job_id so the worker consumes workspace/ overlays.
      // ROOT CAUSE: previously overlay_job_id was omitted → worker compiled from svcgen/base
      // every time → workspace edits from AI Resolve were ignored → issue count flipped.
      const _traceOverlayId = gateType === 'compile' ? currentJobIdRef.current : null
      console.log('[PREFLIGHT_TRACE]', {
        action: 'build_rerun', gateType, scope: 'focused',
        jobIdUsed: _traceOverlayId, overlayJobIdSent: _traceOverlayId,
        note: _traceOverlayId
          ? 'overlay_job_id sent — worker will apply workspace/ overlays'
          : 'no overlay_job_id (structural gate)',
      })
      if (!_traceOverlayId && gateType === 'compile')
        console.warn('[PREFLIGHT_TRACE] WARNING: compile rerun has no overlay_job_id — will compile from svcgen base')

      const _rerunBody = { scout_account_id, application_name, user_id }
      if (_traceOverlayId) _rerunBody.overlay_job_id = _traceOverlayId

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(_rerunBody),
      })
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}))
        throw new Error(errBody.detail || `API error: ${res.status}`)
      }
      const { job_id: newJobId } = await res.json()
      showMsg(`Job ${newJobId} queued — polling…`)
      setBuildState(prev => ({ ...prev, outputLines: [...prev.outputLines, `  Job ${newJobId} submitted — awaiting results...`] }))

      // Poll until complete (2 s interval, 9 min max)
      const statusBase = gateType === 'structural'
        ? `${apiBase}/java-validation/status`
        : `${apiBase}/pre-flight/status`

      let passed = false
      for (let i = 0; i < 270; i++) {
        await new Promise(r => setTimeout(r, 2000))
        try {
          const sr = await fetch(`${statusBase}/${newJobId}`, { headers: getAuthHeaders() })
          if (!sr.ok) continue
          const st = await sr.json()
          const norm = (st.status || '').toLowerCase()
          if (['completed', 'succeeded', 'failed'].includes(norm)) {
            passed = st.validation_passed || st.success === true || false
            // Extract decision_summary written by worker DecisionApplier
            if (st.decision_summary) setRerunSummary(st.decision_summary)
            break
          }
        } catch (_) { /* transient network error — retry */ }
      }

      // Refresh issue list from the new job's artifacts
      setCurrentJobId(newJobId)

      if (passed) {
        const elapsed = Math.round((Date.now() - _buildStart) / 1000)
        setBuildState(prev => ({ ...prev, phase: 'success', outputLines: [...prev.outputLines, `[${new Date().toLocaleTimeString()}] PASSED (${elapsed}s)`] }))
        addToast('Build completed successfully', 'success')
        setRerunDone(true)
        showMsg('Check passed — Resume Pipeline now enabled', 5000)
      } else {
        const elapsed = Math.round((Date.now() - _buildStart) / 1000)
        setBuildState(prev => ({ ...prev, phase: 'failed', outputLines: [...prev.outputLines, `[${new Date().toLocaleTimeString()}] FAILED (${elapsed}s)`, `> Fix an issue in the Fix Panel, then click Build again.`] }))
        addToast('Build completed with errors', 'error')
        showMsg('Check failed — review the updated issue list', 5000)
      }
    } catch (e) {
      setBuildState(prev => ({ ...prev, phase: 'failed', outputLines: [...prev.outputLines, `[${new Date().toLocaleTimeString()}] Error: ${e.message}`, `> Check your connection, then try again.`] }))
      addToast(`Build error: ${e.message}`, 'error')
      showMsg(`Re-run error: ${e.message}`)
    } finally {
      setRerunning(false)
      setIssuesStale(false)  // refresh complete — clear stale indicator
    }
  }, [analysisContext, gateType, apiBase, showMsg, addToast])

  // handleRerun: dirty-state + quick-check aware wrapper.
  // Order of checks: unsaved changes → quick check failed → run.
  const handleRerun = useCallback(() => {
    if (editorIsDirty) { setShowSaveRerunModal(true); return }
    if (quickCheckResult?.pass === false) { setShowQuickCheckRerunModal(true); return }
    executeRerun()
  }, [editorIsDirty, quickCheckResult, executeRerun])

  const handleSaveAndRerun = useCallback(async () => {
    setShowSaveRerunModal(false)
    try { await editorRef.current?.triggerSave?.() } catch (_) { /* save error shown in editor */ }
    executeRerun()
  }, [executeRerun])

  const handleRerunWithoutSaving = useCallback(() => {
    setShowSaveRerunModal(false)
    executeRerun()
  }, [executeRerun])

  // Header Save button — writes current file to workspace/ via editor ref.
  const handleHeaderSave = useCallback(async () => {
    if (!editorRef.current?.triggerSave) return
    setSaving(true)
    try {
      await editorRef.current.triggerSave()
    } catch (_) {
      // save error shown by the editor itself
    } finally {
      setSaving(false)
    }
  }, [])

  // triggerAutoRefresh: called after any Monaco-modifying action (Mark TODO, Comment Out,
  // Block Comment, AI Apply). Sets issuesStale=true so the "Refreshing…" indicator shows,
  // then fires executeRerun() if not already running so the issue list reloads with
  // corrected line numbers after the line shift.
  const triggerAutoRefresh = useCallback(() => {
    setIssuesStale(true)
    if (!rerunningRef.current) {
      console.log(' [ResolveIssuesView] Auto-refresh: issues stale, triggering gate re-run')
      executeRerun()
    }
  }, [executeRerun])
  triggerAutoRefreshRef.current = triggerAutoRefresh

  // Apply the structured AI patch, await workspace save, auto-close modal, then re-run.
  // handleAIApply already triggers triggerAutoRefresh (which starts a rerun) — but we
  // need the workspace write committed BEFORE that rerun competes so the editor loads the
  // patched file on reload and the dirty flag is cleared (prevents "Unsaved Changes" modal later).
  const handleAIApplyAndRerun = useCallback(async (patchObj) => {
    if (!patchObj) return
    // Step 1: apply patch to Monaco + persist GateDecision to DB + background auto-refresh rerun
    handleAIApply(patchObj)

    // Step 2: await workspace save (wait one rAF so React re-renders with new Monaco content)
    setAiSaving(true)
    try {
      await new Promise(resolve => requestAnimationFrame(resolve))
      await editorRef.current?.triggerSave?.()
      console.log(' [AIApplyAndRerun] Workspace save complete — proceeding to rerun')
    } catch (err) {
      // Save failed — log but do NOT block. GateDecision is in DB so rerun still applies patch.
      console.warn(' [AIApplyAndRerun] Save failed (non-blocking):', err?.message)
      showMsg(`Workspace save failed — patch recorded, rerun proceeding (${err?.message})`)
    } finally {
      setAiSaving(false)
    }

    // Step 3: auto-close modal (rerun already started via triggerAutoRefresh in handleAIApply)
    setAiPanelOpen(false)
  }, [handleAIApply, showMsg])

  // ── Manual edit persistence ───────────────────────────────────────────────
  // Called by CodeEditorView after a successful Save with the changed line ranges.
  // Persists each change as a CUSTOM_EDIT GateDecision so worker DecisionApplier applies it.
  const handleManualEditSave = useCallback((changedRanges, _newContent, fileInfo) => {
    if (!changedRanges.length || !fileInfo || !scout_account_id) return
    const filename = fileInfo.name || fileInfo.path?.split('/').pop() || 'unknown.java'

    for (const range of changedRanges) {
      let contextBefore = [], contextAfter = []
      const wCtx = editorRef.current?.getCodeAroundLine?.(range.startLine, 3)
      if (wCtx?.text) {
        const ls = wCtx.text.split('\n')
        const off = range.startLine - wCtx.startLine
        contextBefore = ls.slice(0, off).map(l => l.trim()).filter(Boolean)
        contextAfter  = ls.slice(off + (range.endLine - range.startLine + 1))
                           .map(l => l.trim()).filter(Boolean)
      }

      const patch = {
        start_line:       range.startLine,
        end_line:         range.endLine,
        replacement_text: range.newLines.join('\n'),
        original_line:    (range.originalLines[0] || '').trim(),
        context_before:   contextBefore,
        context_after:    contextAfter,
        target_file:      filename,
      }

      // Match to a known issue by line proximity (±3) so resolution badge updates
      const matched = issues.find(iss => {
        const l = iss.javaLocation?.line
        return l && Math.abs(l - range.startLine) <= 3
      })

      if (matched) {
        applyFix(matched.id, patch, buildApiContext(matched))
        recordChange('fix_applied', matched, 'Manual edit saved')
      } else {
        // Synthetic fingerprint for edits not matching any listed issue
        const fp = safeBase64(
          `manual:${gateType}:${filename}:${range.startLine}:${patch.original_line}`.slice(0, 200)
        ).slice(0, 64)
        upsertDecision({
          account_id: scout_account_id, application_name, user_id,
          gate_type: gateType, fingerprint: fp,
          decision_type: 'CUSTOM_EDIT', target_file: filename,
          patch_json: JSON.stringify(patch),
        }).catch(err => console.error('[ResolveIssuesView] Manual edit decision persist failed:', err))
      }

      console.log(` [ResolveIssuesView] Saved overlay for ${filename} gate=${gateType} line=${range.startLine}`)
      // [DEBUG/TRACE] A) UI — task A.1/A.2: manual editor PUT; actual write path in API logs
      console.log('[PREFLIGHT_TRACE]', {
        action: 'editor_put_manual', gateType, filename, line: range.startLine,
        jobIdUsed: currentJobIdRef.current,
        note: 'next Build will use this jobId as overlay_job_id',
      })
    }
    // File was saved — mark issues stale so UI shows indicator; Build remains a manual action
    setIssuesStale(true)
  }, [gateType, issues, editorRef, applyFix, buildApiContext, recordChange,
      scout_account_id, application_name, user_id])

  // ── Quick action: Comment Out Line ────────────────────────────────────────
  const handleCommentOutLine = useCallback(() => {
    if (!selectedIssue) return
    const line = selectedIssue.javaLocation?.line || 1
    const ctx = editorRef.current?.getCodeAroundLine?.(line, 0)
    if (!ctx || ctx.text === undefined) {
      showMsg('Editor not ready — navigate to the issue first')
      return
    }

    const originalLine = ctx.text
    const indent    = originalLine.match(/^(\s*)/)?.[1] || '        '
    const trimmed   = originalLine.trim()
    const commented = `${indent}// ${trimmed}`

    let contextBefore = [], contextAfter = []
    const wCtx = editorRef.current?.getCodeAroundLine?.(line, 3)
    if (wCtx?.text) {
      const ls = wCtx.text.split('\n'), off = line - wCtx.startLine
      contextBefore = ls.slice(0, off).map(l => l.trim()).filter(Boolean)
      contextAfter  = ls.slice(off + 1).map(l => l.trim()).filter(Boolean)
    }

    editorRef.current?.insertCodeAtLine?.(commented, line)
    applyFix(selectedIssue.id, {
      start_line: line, end_line: line,
      replacement_text: commented, original_line: trimmed,
      context_before: contextBefore, context_after: contextAfter,
      target_file: selectedIssue.javaLocation?.file || '',
    }, buildApiContext(selectedIssue))

    recordChange('fix_applied', selectedIssue, 'Commented out line')
    showMsg('Line commented out and decision saved.')
    triggerAutoRefreshRef.current?.()
  }, [selectedIssue, editorRef, applyFix, buildApiContext, recordChange, showMsg])

  // ── Quick action: Block Comment Selection ────────────────────────────────
  const handleBlockCommentSelection = useCallback(() => {
    const sel = editorRef.current?.getSelectedRange?.()
    if (!sel || sel.isEmpty) {
      showMsg('Select lines in the Java editor first, then click Block Comment.')
      return
    }

    const { startLine, endLine } = sel
    const content = editorRef.current?.getFileContent?.() || ''
    const selectedLines = content.split('\n').slice(startLine - 1, endLine)
    if (!selectedLines.length) return

    const indent  = selectedLines[0].match(/^(\s*)/)?.[1] || '        '
    const wrapped = selectedLines.length === 1
      ? `${indent}/* ${selectedLines[0].trim()} */`
      : `${indent}/*\n${selectedLines.map(l => `${indent} * ${l.trim()}`).join('\n')}\n${indent} */`

    let contextBefore = [], contextAfter = []
    const wB = editorRef.current?.getCodeAroundLine?.(startLine, 3)
    if (wB?.text) {
      const ls = wB.text.split('\n')
      contextBefore = ls.slice(0, startLine - wB.startLine).map(l => l.trim()).filter(Boolean)
    }
    const wA = editorRef.current?.getCodeAroundLine?.(endLine, 3)
    if (wA?.text) {
      const ls = wA.text.split('\n')
      contextAfter = ls.slice(endLine - wA.startLine + 1).map(l => l.trim()).filter(Boolean)
    }

    editorRef.current?.replaceLineRange?.(startLine, endLine, wrapped)

    const matched = (selectedIssue?.javaLocation?.line ?? -1) >= startLine &&
                    (selectedIssue?.javaLocation?.line ?? -1) <= endLine ? selectedIssue : null
    const patch = {
      start_line: startLine, end_line: endLine,
      replacement_text: wrapped,
      original_line: (selectedLines[0] || '').trim(),
      context_before: contextBefore, context_after: contextAfter,
      target_file: matched?.javaLocation?.file || selectedIssue?.javaLocation?.file || '',
    }

    if (matched) {
      applyFix(matched.id, patch, buildApiContext(matched))
      recordChange('fix_applied', matched, 'Block commented selection')
    } else {
      const fp = safeBase64(
        `manual:block:${gateType}:${startLine}:${patch.original_line}`.slice(0, 200)
      ).slice(0, 64)
      upsertDecision({
        account_id: scout_account_id, application_name, user_id,
        gate_type: gateType, fingerprint: fp,
        decision_type: 'CUSTOM_EDIT', target_file: patch.target_file,
        patch_json: JSON.stringify(patch),
      }).catch(err => console.error('[ResolveIssuesView] Block comment persist failed:', err))
    }
    showMsg('Block comment applied and decision saved.')
    triggerAutoRefreshRef.current?.()
  }, [selectedIssue, editorRef, gateType, applyFix, buildApiContext, recordChange,
      showMsg, scout_account_id, application_name, user_id])

  // Clipboard write is handled inside AISuggestionModal; this just shows the toast.
  const handleAICopy = useCallback(() => {
    showMsg('Copied to clipboard')
  }, [showMsg])

  const handleAIPanelClose = useCallback(() => {
    setAiPanelOpen(false)
  }, [])

  const handleAIRegenerate = useCallback(() => {
    handleAIGenerate()
  }, [handleAIGenerate])

  const handleResumePipeline = useCallback(() => {
    if (onResumePipeline && currentJobId) {
      onResumePipeline(currentJobId, gateType)
    } else {
      onBack()
    }
  }, [onResumePipeline, currentJobId, gateType, onBack])

  // ── Playbook: Jump to End of File ────────────────────────────────────────
  const handleJumpToEndOfFile = useCallback(() => {
    editorRef.current?.jumpToEndOfFile?.()
  }, [])

  // ── Playbook: Check Brace Balance ────────────────────────────────────────
  const handleCheckBraces = useCallback(() => {
    const content = editorRef.current?.getFileContent?.() || ''
    if (!content) { showMsg('Load a file in the editor first'); return }
    const open  = (content.match(/\{/g) || []).length
    const close = (content.match(/\}/g) || []).length
    setBraceBalance(open - close)
    console.log(` [ResolveIssuesView] Brace check: open=${open} close=${close} balance=${open - close}`)
  }, [showMsg])

  // ── Quick Check — fast client-side structural sanity check ────────────────
  const handleQuickCheck = useCallback(() => {
    const content = editorRef.current?.getFileContent?.() || ''
    if (!content) { showMsg('Load a Java file in the editor first'); return }
    setQuickChecking(true)
    // Run synchronously in a microtask so React renders the loading state first
    setTimeout(() => {
      const result = runQuickCheck(content)
      setQuickCheckResult(result)
      setQuickChecking(false)
      console.log(` [ResolveIssuesView] Quick Check: pass=${result.pass} errors=${result.errors.length}`)
      if (result.pass) showMsg('Quick Check passed — file looks structurally valid.')
    }, 0)
  }, [showMsg])

  // Reset brace balance + quick check result when the Java file changes
  useEffect(() => {
    setBraceBalance(null)
    setQuickCheckResult(null)
  }, [externalNavTarget?.javaFile])

  // NOTE: externalNavTarget → editorTabs sync effect removed (2026-03-18).
  // All navigation goes through openTab(), which creates/activates the tab and
  // sets externalNavTarget in a single synchronous call. No secondary effect needed.

  // Tick elapsed seconds while build is running
  useEffect(() => {
    if (buildState.phase !== 'running' || !buildState.startedAt) { setElapsedSecs(0); return }
    const iv = setInterval(
      () => setElapsedSecs(Math.floor((Date.now() - buildState.startedAt) / 1000)),
      1000
    )
    return () => clearInterval(iv)
  }, [buildState.phase, buildState.startedAt])

  // ── Monaco command palette handlers (MZ: prefix actions) ──────────────────
  const paletteCommandHandlers = useMemo(() => ({
    'ai-repair-method':         handleAIRepairMethod,
    'ai-repair-region':         handleAIRepairRegion,
    'ai-repair-file-structure': handleAIRepairFileStructure,
    'ai-safe-patch':            handleAskAI,
    'apply-stub':               handleMarkDeferred,
    'comment-out-line':         handleCommentOutLine,
    'block-comment':            handleBlockCommentSelection,
    'save-file':                () => editorRef.current?.triggerSave?.(),
    'rerun-check':              handleRerun,
    'explain-issue':            handleExplainIssue,
    'jump-to-end-of-file':      handleJumpToEndOfFile,
    'quick-check':              handleQuickCheck,
  }), [
    handleAIRepairMethod, handleAIRepairRegion, handleAIRepairFileStructure,
    handleAskAI, handleMarkDeferred, handleCommentOutLine, handleBlockCommentSelection,
    handleRerun, handleExplainIssue, handleJumpToEndOfFile, handleQuickCheck,
  ])

  // ── Derived state ──────────────────────────────────────────────────────────
  const blockerCount  = issues.filter(i => i.severity === 'BLOCK').length
  const unresolved    = unresolvedCount(issues.filter(i => i.severity === 'BLOCK'))
  const canResume     = rerunDone
  const GateIcon      = (gateType === 'compile' || gateType === 'package') ? Zap : ShieldCheck
  // For compile gate: load from compile_failure_sources (pre_flight job).
  // Fallback chain: compile → service-generation → analysis.
  const editorSource  = (() => {
    const preferred = EDITOR_SOURCE[gateType] || 'service-generation'
    if (preferred === 'package' && !analysisContext?.jobs?.java_packaging_job_id) {
      console.log(' [ResolveIssuesView] No java_packaging_job_id — falling back to service-generation')
      if (!analysisContext?.jobs?.service_generation_job_id) {
        console.log(' [ResolveIssuesView] No service_generation_job_id — falling back to analysis')
        return 'analysis'
      }
      return 'service-generation'
    }
    if (preferred === 'compile' && !analysisContext?.jobs?.pre_flight_job_id) {
      console.log(' [ResolveIssuesView] No pre_flight_job_id — falling back to service-generation')
      if (!analysisContext?.jobs?.service_generation_job_id) {
        console.log(' [ResolveIssuesView] No service_generation_job_id — falling back to analysis')
        return 'analysis'
      }
      return 'service-generation'
    }
    if (preferred === 'service-generation' && !analysisContext?.jobs?.service_generation_job_id) {
      console.log(' [ResolveIssuesView] No service_generation_job_id — falling back to analysis source')
      return 'analysis'
    }
    return preferred
  })()

  // ── Render ─────────────────────────────────────────────────────────────────
  //
  // WorkbenchShell slot definitions.
  // All JSX content is identical to before — just organized as named slots
  // so the shared WorkbenchShell can be reused across future workbench modes.
  //
  // Shell owns: layout chrome, Allotment, sidebar collapse, toasts, output drawer.
  // This component owns: all data, actions, and mode-specific content below.

  const wbHeaderIcon = <GateIcon size={14} className="rv-header-icon" />

  const wbHeaderStatus = blockerCount > 0 ? (
    <span className={`rv-status-chip ${unresolved > 0 ? 'blocking' : 'clear'}`}>
      {unresolved > 0
        ? <><XCircle size={11} /> {unresolved} blocker{unresolved !== 1 ? 's' : ''}</>
        : <><CheckCircle size={11} /> Blockers cleared</>
      }
    </span>
  ) : null

  const wbHeaderActions = (
    <>
      <button
        className={`rv-save-btn${editorIsDirty && !saving ? ' dirty' : ''}`}
        disabled={!editorIsDirty || saving}
        onClick={handleHeaderSave}
        title={editorIsDirty ? 'Save current file to workspace/' : 'No unsaved changes'}
      >
        <Save size={13} />
        {saving ? 'Saving\u2026' : 'Save'}
      </button>

      <button
        className="rv-build-btn"
        disabled={rerunning}
        onClick={handleRerun}
        title="Re-run compile check against workspace/"
      >
        {rerunning
          ? <Loader2 size={13} className="rv-build-spin" />
          : <Play size={13} />
        }
        {rerunning ? 'Building\u2026' : 'Build'}
      </button>

      {gateType === 'compile' && currentJobId && (
        <button
          className="rv-reindex-btn"
          disabled={reindexing}
          onClick={handleReindex}
          title="Rebuild symbol index from current workspace files"
        >
          <RefreshCw size={13} className={reindexing ? 'rv-build-spin' : ''} />
          {reindexing ? 'Indexing\u2026' : 'Re-index'}
        </button>
      )}

      {canResume && unresolved === 0 && (
        <button
          className="rv-resume-btn active"
          onClick={handleResumePipeline}
          title="Resume pipeline"
        >
          <CheckCircle size={13} />
          Resume Pipeline
        </button>
      )}
    </>
  )

  const wbStatusBar = (
    <div className={`rv-status-bar rv-status-bar--${buildState.phase}`}>
      {buildState.phase === 'idle' && (
        <span className="rv-sb-text">Ready</span>
      )}
      {buildState.phase === 'running' && (
        <>
          <Loader2 size={12} className="rv-sb-spin" />
          <span className="rv-sb-text">Build started\u2026 (Compiling)</span>
          <span className="rv-sb-sep">\u2022</span>
          <span className="rv-sb-elapsed">{elapsedSecs}s</span>
          <span className="rv-sb-sep">\u2022</span>
          <button className="rv-sb-link" onClick={() => setOutputDrawerOpen(true)}>View output</button>
        </>
      )}
      {buildState.phase === 'failed' && (
        <>
          <XCircle size={12} className="rv-sb-icon--fail" />
          <span className="rv-sb-text">Build failed</span>
          {unresolved > 0 && (
            <>
              <span className="rv-sb-sep">\u2022</span>
              <span className="rv-sb-err">{unresolved} error{unresolved !== 1 ? 's' : ''}</span>
            </>
          )}
          <span className="rv-sb-sep">\u2022</span>
          <button className="rv-sb-link" onClick={() => setOutputDrawerOpen(true)}>View errors</button>
        </>
      )}
      {buildState.phase === 'success' && (
        <>
          <CheckCircle size={12} className="rv-sb-icon--pass" />
          <span className="rv-sb-text">Build passed</span>
          <span className="rv-sb-sep">\u2022</span>
          <span className="rv-sb-ok">0 blockers</span>
          {canResume && (
            <>
              <span className="rv-sb-sep">\u2022</span>
              <button className="rv-sb-link" onClick={handleResumePipeline}>Resume pipeline</button>
            </>
          )}
        </>
      )}
    </div>
  )

  const wbSidebarLabel =
    sidebarTab === 'explorer' ? 'Explorer'
    : sidebarTab === 'issues' ? 'Issues'
    : sidebarTab === 'files'  ? 'Files'
    : 'Changes'

  // ── Left rail slot — sidebar with Explorer / Issues / Changes / Files tabs ──
  const wbLeftRail = (
    <div className="rv-sidebar">

      {/* Tab header */}
      <div className="rv-sidebar-tabs">
        <div
          className={`rv-tabs-scroll${tabsHasOverflow ? ' rv-tabs-scroll--overflow' : ''}`}
          ref={tabsScrollRef}
        >
          <button
            className={`rv-sidebar-tab ${sidebarTab === 'explorer' ? 'active' : ''}`}
            onClick={() => setSidebarTab('explorer')}
            title="Project file explorer"
          >
            Explorer
          </button>
          <button
            className={`rv-sidebar-tab ${sidebarTab === 'issues' ? 'active' : ''}`}
            onClick={() => setSidebarTab('issues')}
          >
            Issues
            <span className="rv-issue-count">{issues.length}</span>
          </button>
          <button
            className={`rv-sidebar-tab ${sidebarTab === 'changes' ? 'active' : ''}`}
            onClick={() => setSidebarTab('changes')}
          >
            Changes
            {changesLog.length > 0 && <span className="rv-issue-count">{changesLog.length}</span>}
          </button>
          <button
            className={`rv-sidebar-tab ${sidebarTab === 'files' ? 'active' : ''}`}
            onClick={() => setSidebarTab('files')}
            title="Issues grouped by Java file"
          >
            Files
            {fileGroups.length > 0 && <span className="rv-issue-count">{fileGroups.length}</span>}
          </button>
        </div>
        <button
          className="rv-collapse-btn"
          onClick={() => {
            setSidebarCollapsed(true)
            console.log('\uD83C\uDFB8 [ResolveIssuesView] sidebar collapsed')
          }}
          title="Collapse Issues Panel"
        >
          <PanelLeftClose size={14} />
        </button>
      </div>

      {/* Tab content */}
      {sidebarTab === 'explorer' && (
        <div className="rv-explorer-tab">
          {gateJobIdForExplorer ? (
            <ProjectExplorer
              gateJobId={gateJobIdForExplorer}
              refreshKey={explorerRefreshKey}
              scope={editorScope}
              onScopeChange={handleScopeChange}
              onFileOpen={handleExplorerFileOpen}
              onFileDoubleClick={handleExplorerFileDoubleClick}
              errorFiles={issues.map(i => i.javaLocation?.file).filter(Boolean)}
              onRecompile={handleRerun}
              onAIResolveFile={(item) => {
                const basename = item.path.split('/').pop()
                const fileIssue = issues.find(i => i.javaLocation?.file === basename)
                if (fileIssue) {
                  setSelectedIssue(fileIssue)
                  setTimeout(() => aiResolveIssue(fileIssue, 'auto'), 100)
                }
              }}
              onExplainIssue={(item) => {
                const basename = item.path.split('/').pop()
                const fileIssue = issues.find(i => i.javaLocation?.file === basename)
                if (fileIssue) {
                  setSelectedIssue(fileIssue)
                  handleExplainIssue()
                }
              }}
            />
          ) : (
            <div className="rv-list-state">No compile gate job available for file explorer.</div>
          )}
        </div>
      )}

      {sidebarTab === 'issues' && (
        <div className="rv-explorer">
          {issuesStale && rerunning && (
            <div className="rv-stale-banner">
              <Loader2 size={11} className="rv-spin" />
              Refreshing line numbers\u2026
            </div>
          )}
          {showStructuralBanner && (
            <div className="rv-cascade-banner">
              <AlertTriangle size={11} />
              Structural errors can mask missing-symbol errors \u2014 fix braces/structure first to reveal the true error set
            </div>
          )}
          {symbolIndex && uniqueSymbolCount > 0 && issues.length > uniqueSymbolCount && (
            <div className="rv-symbol-summary">
              <Puzzle size={10} />
              {issues.length} errors \u2192 {uniqueSymbolCount} unique missing symbol{uniqueSymbolCount !== 1 ? 's' : ''}
            </div>
          )}
          <div className="rv-issue-list">
            {loading && (
              <div className="rv-list-state">
                <Loader2 size={14} className="rv-spin" />
                Loading\u2026
              </div>
            )}

            {!loading && issues.length === 0 && (
              <div className="rv-list-state">
                {currentJobId
                  ? 'No issue artifacts found for this job.'
                  : `Run ${GATE_LABELS[gateType] || 'the gate check'} first.`
                }
              </div>
            )}

            {issues.map(issue => (
              <IssueRow
                key={issue.id}
                issue={issue}
                selected={selectedIssue?.id === issue.id}
                resolution={resolutions[issue.id]}
                onClick={() => handleSelectIssue(issue)}
                onGenerateStub={gateJobIdForExplorer ? handleStubFromIssue : null}
              />
            ))}
          </div>
        </div>
      )}

      {sidebarTab === 'changes' && (
        <div className="rv-changes-list">
          {changesLog.length === 0 ? (
            <div className="rv-list-state">
              No changes recorded yet. Apply fixes or mark issues to see activity here.
            </div>
          ) : (
            changesLog.map(change => (
              <ChangeRow
                key={change.id}
                change={change}
                onClick={() => handleChangeClick(change)}
              />
            ))
          )}
        </div>
      )}

      {sidebarTab === 'files' && (
        <div className="rv-files-list">
          {issues.length === 0 ? (
            <div className="rv-list-state">
              {loading ? (
                <><Loader2 size={14} className="rv-spin" /> Loading\u2026</>
              ) : (
                currentJobId ? 'No issue artifacts found for this job.' : `Run ${GATE_LABELS[gateType] || 'the gate check'} first.`
              )}
            </div>
          ) : (
            fileGroups.map(({ file, issues: fileIssues, blockers, warnings }) => {
              const expanded = fileGroupsExpanded[file] !== false
              return (
                <div key={file} className="rv-file-group">
                  <button
                    className="rv-file-group-header"
                    onClick={() => setFileGroupsExpanded(prev => ({ ...prev, [file]: !expanded }))}
                    title={file}
                  >
                    <ChevronRight size={11} className={`rv-file-chevron${expanded ? ' open' : ''}`} />
                    <FileCode size={11} className="rv-file-icon" />
                    <span className="rv-file-name">{file}</span>
                    <span className="rv-file-counts">
                      {blockers > 0 && <span className="rv-file-count block">{blockers}</span>}
                      {warnings > 0 && <span className="rv-file-count warn">{warnings}</span>}
                    </span>
                  </button>
                  {expanded && (
                    <div className="rv-file-issues">
                      {fileIssues.map(issue => (
                        <button
                          key={issue.id}
                          className={`rv-file-issue-row${selectedIssue?.id === issue.id ? ' selected' : ''}`}
                          onClick={() => { handleSelectIssue(issue); setSidebarTab('issues') }}
                          title={issue.summary}
                        >
                          <span className={`rv-issue-sev ${issue.severity === 'BLOCK' ? 'block' : 'warn'}`}>
                            {issue.severity === 'BLOCK'
                              ? <XCircle size={10} />
                              : <AlertTriangle size={10} />
                            }
                          </span>
                          <span className="rv-file-issue-line">
                            {issue.javaLocation?.line ? `L${issue.javaLocation.line}` : ''}
                          </span>
                          <span className="rv-file-issue-text">{issue.summary}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Run summary */}
      {rerunSummary && (
        <div className="rv-rerun-summary">
          <CheckCircle size={12} />
          {rerunSummary.applied ?? 0} decision{(rerunSummary.applied ?? 0) !== 1 ? 's' : ''} applied
          {rerunSummary.suppressed > 0 && `, ${rerunSummary.suppressed} suppressed`}
          {rerunSummary.skipped > 0 && ` \u2014 ${rerunSummary.skipped} skipped`}
        </div>
      )}

      {/* Single-file hint */}
      {issues.length > 0 && distinctJavaFiles <= 1 && (
        <div className="rv-single-file-hint">
          <FileCode size={10} />
          {distinctJavaFiles === 0
            ? 'No Java file paths found in this report.'
            : '1 Java source in gate artifacts \u2014 other files not emitted to compile_failure_sources.'}
        </div>
      )}

      {/* Fix Panel */}
      <FixPanel
        issue={selectedIssue}
        resolution={selectedIssue ? resolutions[selectedIssue.id] : null}
        onApplyFix={handleApplyFix}
        onMarkDeferred={handleMarkDeferred}
        onExplainIssue={handleExplainIssue}
        onCommentOutLine={handleCommentOutLine}
        onBlockCommentSelection={handleBlockCommentSelection}
        onAskAI={handleAskAI}
        onAIRepairMethod={handleAIRepairMethod}
        onAIRepairRegion={handleAIRepairRegion}
        onAIRepairFileStructure={handleAIRepairFileStructure}
        rerunning={rerunning}
        onRerun={handleRerun}
        actionMsg={actionMsg}
        onQuickCheck={handleQuickCheck}
        quickChecking={quickChecking}
        quickCheckResult={quickCheckResult}
        onAIResolveAuto={handleAIResolveAuto}
        aiResolving={aiResolving}
        onFixNow={handleFixNow}
        onGenerateStub={handleGenerateStub}
        onMarkExternal={handleMarkExternal}
        onStrictCheck={() => handleCodeCheck('strict')}
        symbolHint={selectedIssue ? getSymbolHint(selectedIssue, symbolIndex) : null}
      />

      {/* Manual Fix Playbook */}
      <StructuralPlaybook
        open={playbookOpen}
        onToggle={() => setPlaybookOpen(p => !p)}
        onJumpToEnd={handleJumpToEndOfFile}
        onCheckBraces={handleCheckBraces}
        braceBalance={braceBalance}
      />

    </div>
  )

  // ── Center content slot — editor pane ──────────────────────────────────────
  const wbCenterContent = (
    <div className="rv-editor-pane">
      <EditorTabBar
        tabs={editorTabs}
        activeTabPath={activeTabPath}
        dirtyPaths={dirtyPaths}
        onActivate={handleTabActivate}
        onClose={handleTabClose}
      />

      {activeTabPath && (
        <div className="rv-breadcrumb">
          {activeTabPath.split('/').map((seg, i, arr) => (
            <span key={i} className="rv-bc-seg">
              {i > 0 && <span className="rv-bc-sep">\u203a</span>}
              {i < arr.length - 1
                ? <span className="rv-bc-dir">{seg}</span>
                : <span className="rv-bc-file">{seg}</span>
              }
            </span>
          ))}
        </div>
      )}

      {hasJobData ? (
        <CodeEditorView
          ref={editorRef}
          onBack={onBack}
          source={editorSource}
          analysisContext={effectiveAnalysisContext}
          hideHeader={true}
          hideEditorToolbar={true}
          externalNavTarget={externalNavTarget}
          activeJavaFile={activeTabPath}
          onManualEditSave={handleManualEditSave}
          onDirtyChange={handleDirtyChange}
          editorScope={editorScope}
          onScopeChange={handleScopeChange}
          onCodeCheck={handleCodeCheck}
          upstreamJobId={upstreamJobId}
          upstreamJobType={upstreamJobType}
          paletteCommandHandlers={paletteCommandHandlers}
          gateIssues={issues}
        />
      ) : (
        <EditorPlaceholder gateType={gateType} />
      )}
    </div>
  )

  // ── Output drawer content slot ─────────────────────────────────────────────
  const wbOutputContent = (
    <>
      <div className="rv-output-header">
        <span className="rv-output-title">Build Output</span>
        {buildState.startedAt && (
          <span className="rv-output-summary">
            <span className="rv-output-gate">{GATE_LABELS[gateType] ?? gateType}</span>
            <span className="rv-output-sep"> \u00b7 </span>
            <span className={`rv-output-status rv-output-status--${buildState.phase}`}>
              {buildState.phase === 'running' ? 'Running' : buildState.phase === 'success' ? 'Passed' : 'Failed'}
            </span>
          </span>
        )}
        <div className="rv-output-actions">
          <button
            className="rv-output-btn"
            onClick={() => navigator.clipboard?.writeText(buildState.outputLines.join('\n'))}
            title="Copy to clipboard"
          >Copy</button>
          <button
            className="rv-output-btn"
            onClick={() => setBuildState(prev => ({ ...prev, outputLines: [] }))}
            title="Clear output"
          >Clear</button>
          <button className="rv-output-btn" onClick={() => setOutputDrawerOpen(false)}>Close</button>
        </div>
      </div>
      <div className="rv-output-body">
        {buildState.outputLines.length === 0
          ? <span className="rv-output-empty">No output yet. Run a Build to see results.</span>
          : buildState.outputLines.map((line, i) => {
              const isHint = line.startsWith('> ')
              return (
                <div key={i} className={`rv-output-line${isHint ? ' rv-output-line--hint' : ''}`}>
                  {isHint ? line.slice(2) : line}
                </div>
              )
            })
        }
      </div>
    </>
  )

  return (
    <WorkbenchShell
      onBack={onBack}
      title="Code Resolution Workbench"
      contextTag={GATE_LABELS[gateType]}
      headerIcon={wbHeaderIcon}
      headerStatus={wbHeaderStatus}
      headerActions={wbHeaderActions}
      statusBar={wbStatusBar}
      sidebarCollapsed={sidebarCollapsed}
      onSidebarCollapse={() => { setSidebarCollapsed(true); console.log('\uD83C\uDFB8 [ResolveIssuesView] sidebar collapsed') }}
      onSidebarExpand={() => { setSidebarCollapsed(false); console.log('\uD83C\uDFB8 [ResolveIssuesView] sidebar expanded') }}
      sidebarLabel={wbSidebarLabel}
      leftRail={wbLeftRail}
      centerContent={wbCenterContent}
      outputDrawerOpen={outputDrawerOpen}
      outputDrawerContent={wbOutputContent}
      toasts={toasts}
      onDismissToast={(id) => setToasts(prev => prev.filter(t => t.id !== id))}
    >

      {/* ── Generate Stub dialog ── */}
      {stubIssue && (() => {
        const isClassKind  = !stubKind || stubKind === 'class' || stubKind === 'unknown'
        const isFieldKind  = stubKind === 'field'
        const isArrayKind  = stubKind === 'array'
        const isMethodKind = stubKind === 'method'

        const modalTitle = isMethodKind ? 'Generate Stub Method'
          : isFieldKind  ? 'Generate Stub Field'
          : isArrayKind  ? 'Generate Stub Field (array)'
          : 'Generate Stub Class'

        const fieldLabel = isClassKind ? 'Fully qualified class name' : 'Symbol / identifier name'
        const fieldPlaceholder = isClassKind ? 'com.example.CustomerService' : stubFqn || 'symbolName'

        const TYPE_OPTIONS = ['Object', 'String', 'BigDecimal', 'int', 'boolean', 'long']

        const previewSnippet = (() => {
          const tok = stubFqn.trim() || 'symbolName'
          const t   = stubFieldType || 'Object'
          if (isClassKind) {
            const parts = tok.split('.')
            const cn = parts[parts.length - 1] || 'ClassName'
            const pkg = parts.slice(0, -1).join('.')
            return `${pkg ? `package ${pkg};\n\n` : ''}/**\n * COMPILE-ONLY STUB \u2014 generated by RecodeIT\n * Replace with real implementation.\n */\npublic class ${cn} {\n    // Stub \u2014 no implementation\n}`
          }
          if (isFieldKind)  return `private ${t} ${tok}; // COMPILE-ONLY STUB`
          if (isArrayKind)  return `private ${t}[] ${tok} = new ${t}[0]; // COMPILE-ONLY STUB`
          if (isMethodKind) return `// COMPILE-ONLY STUB\nprivate Object ${tok}() {\n    throw new UnsupportedOperationException("Stub: ${tok} not implemented");\n}`
          return ''
        })()

        const actionLabel = isClassKind ? 'creates a new .java file' : 'inserts into the currently open file'

        return (
          <div className="rv-modal-overlay" onClick={() => { setStubIssue(null); setStubKind(null) }}>
            <div className="rv-stub-modal" onClick={e => e.stopPropagation()}>

              <div className="rv-stub-modal-header">
                <Puzzle size={14} className="rv-stub-modal-icon" />
                <h3 className="rv-stub-modal-title">{modalTitle}</h3>
              </div>

              <div className="rv-stub-modal-ctx">
                {stubIssue?.javaLocation?.file && (
                  <span className="rv-stub-ctx-chip">
                    <span className="rv-stub-ctx-key">File</span>
                    <span className="rv-stub-ctx-val">{stubIssue.javaLocation.file}</span>
                  </span>
                )}
                {stubIssue?.line != null && (
                  <span className="rv-stub-ctx-chip">
                    <span className="rv-stub-ctx-key">Line</span>
                    <span className="rv-stub-ctx-val">{stubIssue.line}</span>
                  </span>
                )}
                <span className="rv-stub-ctx-chip rv-stub-ctx-action">
                  {actionLabel}
                </span>
              </div>

              <div className="rv-stub-modal-body">
                {!stubFqn && (
                  <div className="rv-stub-warn-row">
                    <AlertTriangle size={12} />
                    Could not infer symbol name \u2014 enter it manually below.
                  </div>
                )}

                <div className="rv-stub-field-row">
                  <label className="rv-stub-field-label">{fieldLabel}</label>
                  <input
                    className="rv-stub-fqn-input"
                    value={stubFqn}
                    onChange={e => setStubFqn(e.target.value)}
                    placeholder={fieldPlaceholder}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && stubFqn.trim()) handleStubConfirm(stubFqn.trim(), stubFieldType)
                      if (e.key === 'Escape') { setStubIssue(null); setStubKind(null) }
                    }}
                  />
                  {isClassKind && (
                    <div className="rv-stub-path-hint">
                      {stubFqn.trim()
                        ? <>Will create: <code>src/main/java/{stubFqn.trim().replace(/\./g, '/')}.java</code></>
                        : <span className="rv-stub-path-hint--empty">src/main/java/\u2026/ClassName.java</span>
                      }
                    </div>
                  )}
                </div>

                {(isFieldKind || isArrayKind) && (
                  <div className="rv-stub-field-row">
                    <label className="rv-stub-field-label">Field type</label>
                    <div className="rv-stub-type-picker">
                      {TYPE_OPTIONS.map(t => (
                        <button
                          key={t}
                          className={`rv-stub-type-chip${stubFieldType === t ? ' selected' : ''}`}
                          onClick={() => setStubFieldType(t)}
                          type="button"
                        >{t}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="rv-stub-field-row">
                  <label className="rv-stub-field-label">Preview</label>
                  <pre className="rv-stub-preview">{previewSnippet}</pre>
                </div>

                {!isClassKind && (
                  <div className="rv-stub-insert-note">
                    <Info size={11} />
                    Will be inserted at the best detected position in the open file, then saved to workspace automatically.
                  </div>
                )}
              </div>

              <div className="rv-stub-modal-footer">
                <button className="rv-fix-btn" onClick={() => { setStubIssue(null); setStubKind(null) }}>Cancel</button>
                <button
                  className="rv-fix-btn primary"
                  onClick={() => stubFqn.trim() && handleStubConfirm(stubFqn.trim(), stubFieldType)}
                  disabled={!stubFqn.trim() || stubWorking}
                >
                  {stubWorking ? 'Generating\u2026' : 'Generate Stub'}
                </button>
              </div>

            </div>
          </div>
        )
      })()}

      {/* ── Save & Re-run confirm modal ── */}
      {showSaveRerunModal && (
        <SaveAndRerunModal
          onSaveAndRerun={handleSaveAndRerun}
          onRerunWithout={handleRerunWithoutSaving}
          onCancel={() => setShowSaveRerunModal(false)}
        />
      )}

      {/* ── Quick Check failed — confirm re-run anyway ── */}
      {showQuickCheckRerunModal && (
        <QuickCheckRerunModal
          errors={quickCheckResult?.errors || []}
          onRerunAnyway={() => { setShowQuickCheckRerunModal(false); executeRerun() }}
          onCancel={() => setShowQuickCheckRerunModal(false)}
        />
      )}

      {/* ── AI Resolve (Auto) progress modal ── */}
      {resolveState.open && (
        <AIResolveModal
          resolveState={resolveState}
          onClose={aiCloseModal}
          onCompleted={handleAIResolveModalCompleted}
          onOpenFixAndContinue={() => {
            aiCloseModal()
          }}
        />
      )}

      {/* ── AI Suggestion Modal ── */}
      <AISuggestionModal
        isOpen={aiPanelOpen}
        onClose={handleAIPanelClose}
        issue={selectedIssue}
        gateType={gateType}
        originalCode={aiOriginalCode}
        loading={aiLoading}
        result={aiResult}
        error={aiError}
        applied={aiApplied}
        saving={aiSaving}
        rerunning={rerunning}
        prompt={aiPrompt}
        onPromptChange={setAiPrompt}
        onGenerate={handleAIGenerate}
        onApply={handleAIApply}
        onApplyAndRerun={handleAIApplyAndRerun}
        onApplyAsStub={handleApplyAsStub}
        onCopy={handleAICopy}
        onRegenerate={handleAIRegenerate}
      />

      {/* ── Explain Issue Modal ── */}
      <ExplainIssueModal
        isOpen={explainOpen}
        onClose={() => setExplainOpen(false)}
        issue={selectedIssue}
        loading={explainLoading}
        result={explainResult}
        error={explainError}
      />

    </WorkbenchShell>
  )
}
